import { useEffect, useMemo, useRef, useState } from "react";
import DateInput from "@/components/DateInput";
import api, { fmtMoney, fmtDate, todayISO } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import BancoCombobox from "@/components/BancoCombobox";
import CuentaCombobox from "@/components/CuentaCombobox";
import MoneyInput from "@/components/MoneyInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus, ArrowRight, Trash, UploadSimple, Warning, FileXls, Eye,
  MagnifyingGlass, PencilSimple, FilePdf, DownloadSimple, X, XCircle,
  ListNumbers,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { TIPOS_TRIBUTO } from "@/pages/ComprasPage";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import BatchDetailDialog from "@/components/BatchDetailDialog";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ImputarSaldosDialog from "@/pages/CtaCteImputarSaldosDialog";
import NumberInput from "@/components/ui/NumberInput";
import PdfPreviewDialog from "@/components/PdfPreviewDialog";

const tributoLabel = (t) => TIPOS_TRIBUTO.find((x) => x.value === t)?.label || t;

const emptyCP = () => ({
  nombre: "", cuit: "", email: "", telefono: "", direccion: "", direccion_numero: "",
  localidad: "", provincia: "", codigo_postal: "",
  condicion_iva: "Responsable Inscripto", observaciones: "",
  lista_precios_id: "",
  tipo_proveedor: "productos",   // solo aplica a proveedores
  auto_actualiza_stock: false,   // preset del proveedor para compras
  auto_actualiza_costo: false,   // preset del proveedor para compras
  cuenta_gasto_default: "",      // solo proveedores servicios: cuenta de gasto por defecto
  cuenta_ingreso_default: "",    // solo clientes: cuenta de ingreso por defecto
  grupo: "",                     // solo clientes: grupo (mayorista/minorista/etc)
  cp_tipo: "contado",       // "contado" | "dias" | "dia_semana"
  cp_dias: 30,
  cp_dia_semana: 4,         // 0=Lun..6=Dom (viernes por defecto)
  cp_semanas_offset: 1,     // 0=esta semana, 1=próxima, 2=subsiguiente
});

// Días de la semana (0=Lunes .. 6=Domingo, coincide con date.weekday() en Python)
const DIAS_SEMANA = [
  { value: 0, label: "Lunes" }, { value: 1, label: "Martes" },
  { value: 2, label: "Miércoles" }, { value: 3, label: "Jueves" },
  { value: 4, label: "Viernes" }, { value: 5, label: "Sábado" },
  { value: 6, label: "Domingo" },
];
const SEMANAS_OFFSET = [
  { value: 0, label: "de esta semana" },
  { value: 1, label: "de la semana siguiente" },
  { value: 2, label: "de la subsiguiente" },
];

/** Componente genérico para Cuenta corriente Cliente / Proveedor */
export default function CtaCtePage({ tipo }) {
  const isClientes = tipo === "cliente";
  const label = isClientes ? "Clientes" : "Proveedores";
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [movs, setMovs] = useState(null);
  const [cajas, setCajas] = useState([]);
  const [mediosPago, setMediosPago] = useState([]);
  const [facturasPend, setFacturasPend] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [openMov, setOpenMov] = useState(false);
  const [form, setForm] = useState(emptyCP());
  const [movForm, setMovForm] = useState({
    fecha: todayISO(), caja_id: "", monto: 0, moneda: "ARS",
    facturas_aplicadas: [], concepto: "", retenciones: [],
    // Cheque recibido (cuando el cobro usa caja tipo=cheques)
    cheque_info: { numero: "", banco_code: "", banco_nombre: "", fecha_emision: "", fecha_cobro: "", recibido_por: "", firmante: "", observaciones: "" },
    // Cheques a endosar (cuando el pago usa caja tipo=cheques)
    cheques_endosados_ids: [],
  });
  const [chequesAceptados, setChequesAceptados] = useState([]);
  const [delMov, setDelMov] = useState(null);   // cobro/pago a eliminar
  const [editMov, setEditMov] = useState(null); // cobro/pago a editar
  const [editForm, setEditForm] = useState({ fecha: "", monto: "", concepto: "", caja_id: "" });
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [dupPrompt, setDupPrompt] = useState(null);
  const [batches, setBatches] = useState([]);
  const [tab, setTab] = useState("cta-cte");
  const [cobrosOpen, setCobrosOpen] = useState(false);
  const [cobrosList, setCobrosList] = useState([]);
  const [cobrosLoading, setCobrosLoading] = useState(false);
  const [delBatch, setDelBatch] = useState(null);
  const [viewBatch, setViewBatch] = useState(null);
  const [searchCP, setSearchCP] = useState("");
  const [filterSaldo, setFilterSaldo] = useState("todos"); // "todos" | "con_saldo" | "sin_saldo"
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [delCP, setDelCP] = useState(null);
  const [delCPRefs, setDelCPRefs] = useState(null);
  const [listasPrecios, setListasPrecios] = useState([]);
  const [gruposClientes, setGruposClientes] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [openAjuste, setOpenAjuste] = useState(false);
  const [ajusteForm, setAjusteForm] = useState({ fecha: new Date().toISOString().slice(0, 10), monto: "", concepto: "Saldo inicial", moneda: "ARS" });
  const [ajusteSigno, setAjusteSigno] = useState(1);
  const [savingAjuste, setSavingAjuste] = useState(false);
  const [imputarOpen, setImputarOpen] = useState(false);
  const fileRef = useRef(null);

  const endpoint = isClientes ? "/clientes" : "/proveedores";

  const load = async () => {
    const { data } = await api.get(endpoint);
    setItems(data);
    setSelected((s) => (s ? (data.find((x) => x.id === s.id) || s) : s));
  };

  useEffect(() => {
    setSelected(null); setMovs(null); setSearchCP(""); setFechaDesde(""); setFechaHasta("");
    load();
    api.get("/cajas").then((r) => setCajas(r.data));
    api.get("/medios-pago/mios", { params: { scope: "ventas" } })
      .then((r) => setMediosPago(r.data || []))
      .catch(() => setMediosPago([]));
    api.get("/cuentas").then((r) => setCuentas(r.data || []));
    if (isClientes) {
      api.get("/listas-precios").then((r) => setListasPrecios((r.data || []).sort((a, b) => (a.orden || 0) - (b.orden || 0))));
      api.get("/clientes/grupos").then((r) => setGruposClientes(r.data || [])).catch(() => setGruposClientes([]));
    }
    // eslint-disable-next-line
  }, [tipo]);

  const loadMovimientos = async (cpId, desde, hasta) => {
    const params = {};
    if (desde) params.fecha_desde = desde;
    if (hasta) params.fecha_hasta = hasta;
    const { data } = await api.get(`${endpoint}/${cpId}/movimientos`, { params });
    setMovs(data);
    setFacturasPend(
      (data.facturas || [])
        .filter((f) => f.saldo > 0)
        // Orden por vencimiento ASC (con fallback a fecha) para reflejar el FIFO backend
        .sort((a, b) => {
          const va = a.fecha_vencimiento || a.fecha || "9999-12-31";
          const vb = b.fecha_vencimiento || b.fecha || "9999-12-31";
          return va.localeCompare(vb);
        })
    );
  };

  const openDetail = async (c) => {
    setSelected(c);
    await loadMovimientos(c.id, fechaDesde, fechaHasta);
  };

  // Reload movimientos when date range changes
  useEffect(() => {
    if (selected) loadMovimientos(selected.id, fechaDesde, fechaHasta);
    // eslint-disable-next-line
  }, [fechaDesde, fechaHasta]);

  const openNew = () => {
    setEditingId(null);
    setForm(emptyCP());
    setOpenForm(true);
  };

  const openEdit = (c) => {
    setEditingId(c.id);
    const cp = c.condicion_pago || {};
    setForm({
      nombre: c.nombre || "",
      cuit: c.cuit || "",
      email: c.email || "",
      telefono: c.telefono || "",
      direccion: c.direccion || "",
      direccion_numero: c.direccion_numero || "",
      localidad: c.localidad || "",
      provincia: c.provincia || "",
      codigo_postal: c.codigo_postal || "",
      condicion_iva: c.condicion_iva || "Responsable Inscripto",
      observaciones: c.observaciones || "",
      lista_precios_id: c.lista_precios_id || "",
      tipo_proveedor: c.tipo_proveedor || "productos",
      auto_actualiza_stock: !!c.auto_actualiza_stock,
      auto_actualiza_costo: !!c.auto_actualiza_costo,
      cuenta_gasto_default: c.cuenta_gasto_default || "",
      cuenta_ingreso_default: c.cuenta_ingreso_default || "",
      grupo: c.grupo || "",
      cp_tipo: cp.tipo || "contado",
      cp_dias: cp.dias != null ? cp.dias : 30,
      cp_dia_semana: cp.dia_semana != null ? cp.dia_semana : 4,
      cp_semanas_offset: cp.semanas_offset != null ? cp.semanas_offset : 1,
    });
    setOpenForm(true);
  };

  const confirmDeleteCP = async () => {
    if (!delCP) return;
    try {
      await api.delete(`${endpoint}/${delCP.id}`);
      toast.success(`${isClientes ? "Cliente" : "Proveedor"} eliminado`);
      if (selected?.id === delCP.id) { setSelected(null); setMovs(null); }
      setDelCP(null);
      load();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && detail && typeof detail === "object") {
        setDelCPRefs({ cp: delCP, ...detail });
        setDelCP(null);
      } else {
        toast.error(typeof detail === "string" ? detail : "Error al eliminar");
      }
    }
  };

  const [consultandoPadron, setConsultandoPadron] = useState(false);

  const consultarPadron = async () => {
    const cuitDigits = (form.cuit || "").replace(/\D/g, "");
    if (cuitDigits.length !== 11) {
      toast.error("Ingresá un CUIT de 11 dígitos primero");
      return;
    }
    setConsultandoPadron(true);
    try {
      const { data } = await api.get(`/afip/padron/${cuitDigits}`);
      if (!data.ok) {
        toast.error(data.error || "AFIP no encontró el CUIT");
        return;
      }
      setForm((f) => ({
        ...f,
        nombre: data.razon_social || f.nombre,
        condicion_iva: data.condicion_iva || f.condicion_iva,
        direccion: data.direccion || f.direccion,
        localidad: data.localidad || f.localidad,
        provincia: data.provincia || f.provincia,
        codigo_postal: data.codigo_postal || f.codigo_postal,
      }));
      toast.success(`Datos importados desde AFIP: ${data.razon_social}`);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data?.error;
      toast.error(typeof detail === "string" ? detail : "AFIP: no se pudo consultar");
    } finally {
      setConsultandoPadron(false);
    }
  };

  const saveContraparte = async (e) => {
    e.preventDefault();
    try {
      // Construir condición de pago a partir de los campos individuales
      const cp_tipo = form.cp_tipo || "contado";
      const condicion_pago =
        cp_tipo === "contado"
          ? { tipo: "contado" }
          : cp_tipo === "sin_plazo"
          ? { tipo: "sin_plazo" }
          : cp_tipo === "dias"
          ? { tipo: "dias", dias: Math.max(0, parseInt(form.cp_dias || 0, 10)) }
          : { tipo: "dia_semana",
              dia_semana: parseInt(form.cp_dia_semana || 0, 10),
              semanas_offset: Math.max(0, parseInt(form.cp_semanas_offset || 0, 10)) };
      const payload = {
        nombre: form.nombre, cuit: form.cuit, email: form.email,
        telefono: form.telefono, direccion: form.direccion, direccion_numero: form.direccion_numero,
        localidad: form.localidad, provincia: form.provincia,
        codigo_postal: form.codigo_postal, condicion_iva: form.condicion_iva,
        observaciones: form.observaciones, lista_precios_id: form.lista_precios_id,
        condicion_pago,
      };
      if (!isClientes) {
        payload.tipo_proveedor = form.tipo_proveedor || "productos";
        payload.auto_actualiza_stock = !!form.auto_actualiza_stock;
        payload.auto_actualiza_costo = !!form.auto_actualiza_costo;
        payload.cuenta_gasto_default = form.cuenta_gasto_default || null;
      } else {
        payload.cuenta_ingreso_default = form.cuenta_ingreso_default || null;
        payload.grupo = form.grupo || null;
      }
      if (editingId) {
        await api.put(`${endpoint}/${editingId}`, payload);
        toast.success(`${isClientes ? "Cliente" : "Proveedor"} actualizado`);
      } else {
        await api.post(endpoint, payload);
        toast.success(`${isClientes ? "Cliente" : "Proveedor"} creado`);
      }
      setOpenForm(false);
      setEditingId(null);
      setForm(emptyCP());
      load();
      if (selected && selected.id === editingId) {
        const updated = await api.get(`${endpoint}/${editingId}`);
        setSelected(updated.data);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  // Detectar la caja seleccionada
  const cajaSeleccionada = cajas.find((c) => c.id === movForm.caja_id);
  const esCajaCheques = cajaSeleccionada?.tipo === "cheques";

  // Cargar cheques Aceptados cuando el usuario elige la caja de cheques en un PAGO
  useEffect(() => {
    if (!isClientes && esCajaCheques) {
      api.get("/cheques", { params: { estado: "aceptado" } })
        .then((r) => setChequesAceptados(r.data || []))
        .catch(() => setChequesAceptados([]));
    }
  }, [isClientes, esCajaCheques]);

  // Auto-completar "Recibido por" con el nombre del cliente al abrir el form de cheque
  useEffect(() => {
    if (isClientes && esCajaCheques && selected && !movForm.cheque_info.recibido_por) {
      setMovForm((prev) => ({
        ...prev,
        cheque_info: { ...prev.cheque_info, recibido_por: selected.nombre || "" }
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClientes, esCajaCheques, selected?.id]);

  const registrarMov = async (e) => {
    e.preventDefault();
    const montoNum = parseFloat(movForm.monto || 0);
    if (!montoNum || montoNum <= 0) {
      toast.error("El monto debe ser mayor a 0. No se puede computar un movimiento en cero.");
      return;
    }
    try {
      const payload = {
        tipo: isClientes ? "cobro" : "pago",
        fecha: movForm.fecha,
        contraparte_id: selected.id,
        caja_id: movForm.caja_id,
        monto: montoNum,
        moneda: movForm.moneda,
        facturas_aplicadas: movForm.facturas_aplicadas,
        concepto: movForm.concepto,
      };
      if (isClientes) {
        payload.monto_es_neto = true;
        payload.retenciones = movForm.retenciones
          .filter((r) => parseFloat(r.monto || 0) > 0)
          .map((r) => ({ tipo: r.tipo, monto: parseFloat(r.monto), observacion: r.observacion || null }));
        // Cheque recibido
        if (esCajaCheques) {
          const ci = movForm.cheque_info || {};
          if (!ci.numero || !ci.banco_code || !ci.fecha_emision || !ci.fecha_cobro) {
            toast.error("Complete todos los datos del cheque: nº, banco, fecha emisión y fecha cobro.");
            return;
          }
          payload.cheque_info = {
            numero: ci.numero,
            banco_code: ci.banco_code,
            banco_nombre: ci.banco_nombre,
            fecha_emision: ci.fecha_emision,
            fecha_cobro: ci.fecha_cobro,
            recibido_por: ci.recibido_por || selected?.nombre || null,
            firmante: ci.firmante || null,
            observaciones: ci.observaciones || null,
          };
        }
      } else {
        // Pago con cheques endosados
        if (esCajaCheques) {
          if (!movForm.cheques_endosados_ids || movForm.cheques_endosados_ids.length === 0) {
            toast.error("Seleccioná al menos un cheque en estado 'Aceptado' para endosar.");
            return;
          }
          payload.cheques_endosados_ids = movForm.cheques_endosados_ids;
        }
      }
      await api.post("/cobros-pagos", payload);
      toast.success(isClientes ? "Cobro registrado" : "Pago registrado");
      setOpenMov(false);
      setMovForm({
        fecha: todayISO(), caja_id: "", monto: 0, moneda: "ARS",
        facturas_aplicadas: [], concepto: "", retenciones: [],
        cheque_info: { numero: "", banco_code: "", banco_nombre: "", fecha_emision: "", fecha_cobro: "", recibido_por: "", firmante: "", observaciones: "" },
        cheques_endosados_ids: [],
      });
      load();
      openDetail(selected);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const openEditMov = (raw) => {
    setEditMov(raw);
    setEditForm({
      fecha: raw.fecha,
      monto: raw.monto,
      concepto: raw.concepto || "",
      caja_id: raw.caja_id,
    });
  };

  const submitEditMov = async (e) => {
    e.preventDefault();
    if (!editMov) return;
    const montoNum = parseFloat(editForm.monto || 0);
    if (!montoNum || montoNum <= 0) {
      toast.error("El monto debe ser mayor a 0. No se puede computar un movimiento en cero.");
      return;
    }
    try {
      const payload = {
        fecha: editForm.fecha,
        monto: montoNum,
        concepto: editForm.concepto,
        caja_id: editForm.caja_id,
      };
      await api.put(`/cobros-pagos/${editMov.id}`, payload);
      toast.success("Movimiento actualizado");
      setEditMov(null);
      load();
      if (selected) openDetail(selected);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const confirmDeleteMov = async () => {
    if (!delMov) return;
    try {
      await api.delete(`/cobros-pagos/${delMov.id}`);
      toast.success(`${isClientes ? "Cobro" : "Pago"} eliminado y saldo restaurado`);
      setDelMov(null);
      load();
      if (selected) openDetail(selected);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const toggleFactura = (fid) => {
    const list = movForm.facturas_aplicadas.includes(fid)
      ? movForm.facturas_aplicadas.filter((x) => x !== fid)
      : [...movForm.facturas_aplicadas, fid];
    setMovForm({ ...movForm, facturas_aplicadas: list });
  };

  const addRetencion = () =>
    setMovForm({ ...movForm, retenciones: [...movForm.retenciones, { tipo: "retencion_iva", monto: 0, observacion: "" }] });
  const setRetencion = (i, field, value) => {
    const arr = [...movForm.retenciones];
    arr[i] = { ...arr[i], [field]: value };
    setMovForm({ ...movForm, retenciones: arr });
  };
  const removeRetencion = (i) =>
    setMovForm({ ...movForm, retenciones: movForm.retenciones.filter((_, idx) => idx !== i) });
  const retencionesTotal = useMemo(
    () => movForm.retenciones.reduce((s, r) => s + parseFloat(r.monto || 0), 0),
    [movForm.retenciones]
  );
  const netoCaja = parseFloat(movForm.monto) || 0;
  const totalCobro = netoCaja + retencionesTotal;

  const runImportCobros = async (file, force = false) => {
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(
        `/cobros/import-excel${force ? "?force=true" : ""}`,
        fd, { headers: { "Content-Type": "multipart/form-data" } }
      );
      setImportResult(data);
      setDupPrompt(null);
      toast.success(`${data.cobros_creados} cobros creados · ${data.facturas_procesadas} facturas actualizadas`);
      load();
      if (selected) openDetail(selected);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && detail?.batch) { setDupPrompt({ file, existing: detail.batch }); return; }
      toast.error(typeof detail === "string" ? detail : "Error al importar");
    } finally { setImporting(false); }
  };
  const handleImportCobros = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await runImportCobros(file, false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const loadBatches = async () => {
    const { data } = await api.get("/import-batches");
    setBatches(data.filter((b) => b.type === "cobros"));
  };
  const revertBatch = async () => {
    if (!delBatch) return;
    try {
      const { data } = await api.delete(`/import-batches/${delBatch.id}`);
      toast.success(`Lote revertido: ${data.deleted} cobros eliminados`);
      setDelBatch(null);
      loadBatches(); load();
      if (selected) openDetail(selected);
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };
  useEffect(() => {
    if (isClientes && tab === "historial") loadBatches();
    // eslint-disable-next-line
  }, [tab]);

  const [ajusteEditando, setAjusteEditando] = useState(null); // {id, ...} si estamos editando

  const submitAjuste = async (e) => {
    e.preventDefault();
    if (!selected) return;
    const montoAbs = Math.abs(parseFloat(ajusteForm.monto));
    if (!montoAbs || isNaN(montoAbs)) return toast.error("Ingresá un monto válido (distinto de 0)");
    const monto = montoAbs * (ajusteSigno < 0 ? -1 : 1);
    setSavingAjuste(true);
    try {
      const base = `/${isClientes ? "clientes" : "proveedores"}/${selected.id}/ajuste-deuda`;
      const url = ajusteEditando ? `${base}/${ajusteEditando.id}` : base;
      const method = ajusteEditando ? "put" : "post";
      await api[method](url, {
        fecha: ajusteForm.fecha,
        monto: monto,
        concepto: ajusteForm.concepto || "Saldo inicial",
        moneda: ajusteForm.moneda,
      });
      toast.success(ajusteEditando ? "Ajuste actualizado" : monto < 0 ? "Crédito registrado" : "Deuda registrada");
      setOpenAjuste(false);
      setAjusteEditando(null);
      setAjusteSigno(1);
      setAjusteForm({ fecha: new Date().toISOString().slice(0, 10), monto: "", concepto: "Saldo inicial", moneda: "ARS" });
      load();
      openDetail(selected);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Error al ${ajusteEditando ? "editar" : "registrar"} la deuda`);
    } finally {
      setSavingAjuste(false);
    }
  };

  const openEditAjuste = (fac) => {
    setAjusteEditando(fac);
    setAjusteSigno((fac.total || 0) < 0 ? -1 : 1);
    setAjusteForm({
      fecha: fac.fecha || new Date().toISOString().slice(0, 10),
      monto: String(Math.abs(fac.total || 0) || ""),
      concepto: fac.observaciones || "Saldo inicial",
      moneda: fac.moneda || "ARS",
    });
    setOpenAjuste(true);
  };

  // Anular ajuste (deja marca fiscal)
  const [anulandoAjuste, setAnulandoAjuste] = useState(null);
  const [previewFac, setPreviewFac] = useState(null); // comprobante de venta a previsualizar
  const [anularBusy, setAnularBusy] = useState(false);
  const confirmAnularAjuste = async () => {
    if (!anulandoAjuste || !selected) return;
    setAnularBusy(true);
    try {
      await api.post(
        `/${isClientes ? "clientes" : "proveedores"}/${selected.id}/ajuste-deuda/${anulandoAjuste.id}/anular`,
      );
      toast.success("Ajuste anulado");
      setAnulandoAjuste(null);
      load();
      openDetail(selected);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo anular el ajuste");
    } finally {
      setAnularBusy(false);
    }
  };

  const redondearSaldoARS = async () => {
    if (!selected) return;
    const saldo = selected.saldo_ars || 0;
    if (Math.abs(saldo) < 0.01) return;
    try {
      const { data } = await api.post(
        `/${isClientes ? "clientes" : "proveedores"}/${selected.id}/redondeo`,
        {
          moneda: "ARS",
          concepto: `Redondeo de saldo residual (${saldo.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`,
        },
      );
      const facs = (data?.facturas_impactadas || []).length;
      toast.success(
        facs > 0
          ? `Saldo redondeado a $0 · aplicado a ${facs} factura(s)`
          : "Saldo redondeado a $0",
      );
      load();
      openDetail(selected);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al redondear");
    }
  };

  // Filtro de listado
  const filteredItems = useMemo(() => {
    let list = items;
    if (filterSaldo === "con_saldo") list = list.filter((c) => Math.abs((c.saldo_ars || 0) + (c.saldo_usd || 0)) >= 0.01);
    else if (filterSaldo === "sin_saldo") list = list.filter((c) => Math.abs((c.saldo_ars || 0) + (c.saldo_usd || 0)) < 0.01);
    if (searchCP.trim()) {
      const q = searchCP.toLowerCase();
      list = list.filter((c) =>
        c.nombre.toLowerCase().includes(q) ||
        (c.cuit || "").includes(searchCP) ||
        (c.email || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [items, searchCP, filterSaldo]);

  // Vista unificada de movimientos ordenados por fecha con saldo corriente
  const movimientosUnificados = useMemo(() => {
    if (!movs) return [];
    const facs = (movs.facturas || []).map((f) => {
      // Notas de Crédito y AJUSTES negativos van al HABER (restan saldo). Los
      // comprobantes ANULADOS no mueven el saldo (debe/haber 0) pero se listan
      // con su monto original tachado para trazabilidad.
      const tc = (f.tipo_comprobante || "").toUpperCase();
      const esNC = tc.startsWith("NOTA DE CR");
      const esAjuste = tc === "AJUSTE";
      // Anulada por NC: la factura SIGUE contando (la NC la compensa en el Haber).
      // Anulada sin NC (ej. ajuste anulado): no mueve el saldo.
      const anulada = f.estado === "anulada" && !f.anulada_por_nc_id && !f.anulada_por_nc_numero;
      const monto = Math.abs(f.total || 0);
      const alHaber = esNC || (esAjuste && (f.total || 0) < 0);
      return {
        key: `f-${f.id}`,
        fecha: f.fecha,
        tipo: esNC ? "Nota de Crédito" : esAjuste ? (alHaber ? "Ajuste (crédito)" : "Ajuste") : "Factura",
        referencia: `${f.tipo_comprobante || ""} ${f.numero}`.trim(),
        debe: anulada || alHaber ? 0 : monto,
        haber: anulada || !alHaber ? 0 : monto,
        monto_anulado: anulada ? monto : 0,
        al_haber: alHaber,
        moneda: f.moneda || "ARS",
        concepto: f.observaciones || "",
        raw: f,
        estado: f.estado,
      };
    });
    const key = isClientes ? "cobros" : "pagos";
    const cps = (movs[key] || []).map((m) => ({
      key: `m-${m.id}`,
      fecha: m.fecha,
      tipo: isClientes ? "Cobro" : "Pago",
      referencia: m.caja_nombre,
      debe: 0,
      haber: m.monto,
      moneda: m.moneda || "ARS",
      concepto: m.concepto || "",
      raw: m,
    }));
    // Ordenar por fecha ascendente + insertar orden estable
    const all = [...facs, ...cps].sort((a, b) => a.fecha.localeCompare(b.fecha));
    // Saldo corriente
    let acc = 0;
    return all.map((r) => {
      acc += (r.debe || 0) - (r.haber || 0);
      return { ...r, saldo_acumulado: acc };
    });
  }, [movs, isClientes]);

  const totales = useMemo(() => {
    return movimientosUnificados.reduce(
      (acc, r) => ({ debe: acc.debe + r.debe, haber: acc.haber + r.haber }),
      { debe: 0, haber: 0 }
    );
  }, [movimientosUnificados]);
  const rangoActivo = Boolean(fechaDesde || fechaHasta);

  const recalcularSaldo = async () => {
    if (!selected) return;
    if (!window.confirm("¿Recalcular el saldo guardado a partir de los movimientos de la cuenta corriente (comprobantes vigentes − cobros/pagos)?")) return;
    try {
      const { data } = await api.post(`/${isClientes ? "clientes" : "proveedores"}/${selected.id}/recalcular-saldo`);
      toast.success(`Saldo recalculado: ${fmtMoney(data.anterior.saldo_ars)} → ${fmtMoney(data.nuevo.saldo_ars)}`);
      await load();
      setSelected((s) => (s ? { ...s, ...data.nuevo } : s));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo recalcular");
    }
  };

  const exportExcel = () => {
    if (!selected || movimientosUnificados.length === 0) {
      toast.error("Nada para exportar");
      return;
    }
    const rows = movimientosUnificados.map((r) => ({
      Fecha: fmtDate(r.fecha),
      Tipo: r.tipo,
      Referencia: r.referencia,
      Concepto: r.concepto,
      Debe: r.debe || "",
      Haber: r.haber || "",
      Saldo: r.saldo_acumulado,
      Moneda: r.moneda,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    const rangeLabel = fechaDesde || fechaHasta ? ` ${fechaDesde || "inicio"}_${fechaHasta || "hoy"}` : "";
    XLSX.utils.book_append_sheet(wb, ws, "Cta Cte");
    XLSX.writeFile(wb, `CtaCte_${selected.nombre.replace(/[^\w]/g, "_")}${rangeLabel}.xlsx`);
    toast.success("Excel descargado");
  };

  const exportPDF = () => {
    if (!selected || movimientosUnificados.length === 0) {
      toast.error("Nada para exportar");
      return;
    }
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const title = `Cuenta Corriente - ${selected.nombre}`;
    doc.setFontSize(14);
    doc.text(title, 40, 40);
    doc.setFontSize(9);
    doc.setTextColor(100);
    const meta = [
      `CUIT: ${selected.cuit || "-"}`,
      `Cond. IVA: ${selected.condicion_iva || "-"}`,
      `Período: ${fechaDesde ? fmtDate(fechaDesde) : "inicio"} → ${fechaHasta ? fmtDate(fechaHasta) : "hoy"}`,
      `Emitido: ${new Date().toLocaleString("es-AR")}`,
    ];
    doc.text(meta.join("   ·   "), 40, 58);

    autoTable(doc, {
      startY: 78,
      head: [["Fecha", "Tipo", "Referencia", "Concepto", "Debe", "Haber", "Saldo"]],
      body: movimientosUnificados.map((r) => [
        fmtDate(r.fecha),
        r.tipo,
        r.referencia,
        (r.concepto || "").substring(0, 60),
        r.debe > 0 ? r.debe.toLocaleString("es-AR", { minimumFractionDigits: 2 }) : "",
        r.haber > 0 ? r.haber.toLocaleString("es-AR", { minimumFractionDigits: 2 }) : "",
        r.saldo_acumulado.toLocaleString("es-AR", { minimumFractionDigits: 2 }),
      ]),
      styles: { fontSize: 8, cellPadding: 4 },
      headStyles: { fillColor: [107, 31, 36], textColor: 255, fontStyle: "bold" },
      columnStyles: {
        0: { cellWidth: 60 },
        1: { cellWidth: 55 },
        2: { cellWidth: 130 },
        3: { cellWidth: 230 },
        4: { cellWidth: 80, halign: "right" },
        5: { cellWidth: 80, halign: "right" },
        6: { cellWidth: 90, halign: "right", fontStyle: "bold" },
      },
      foot: [[
        "", "", "", "TOTALES",
        totales.debe.toLocaleString("es-AR", { minimumFractionDigits: 2 }),
        totales.haber.toLocaleString("es-AR", { minimumFractionDigits: 2 }),
        (totales.debe - totales.haber).toLocaleString("es-AR", { minimumFractionDigits: 2 }),
      ]],
      footStyles: { fillColor: [245, 241, 233], textColor: 30, fontStyle: "bold" },
    });

    doc.save(`CtaCte_${selected.nombre.replace(/[^\w]/g, "_")}.pdf`);
    toast.success("PDF descargado");
  };

  const clearFechas = () => { setFechaDesde(""); setFechaHasta(""); };

  return (
    <>
      <Topbar
        title={`Cuentas Corrientes · ${label}`}
        subtitle={`Saldos y movimientos de ${label.toLowerCase()}.`}
        right={
          <div className="flex items-center gap-2">
            {isClientes && (
              <Button
                data-testid="ver-cobros-btn"
                onClick={() => setCobrosOpen(true)}
                variant="outline"
                className="rounded-sm h-9 border-[#6B1F24] text-[#6B1F24] hover:bg-[#F5F1E9]"
              >
                <ListNumbers size={16} className="mr-1" /> Ver cobros
              </Button>
            )}
            <Button data-testid={`new-${tipo}-btn`} onClick={openNew}
              className="rounded-sm bg-[#6B1F24] hover:bg-[#571519] h-9">
              <Plus size={16} className="mr-1" /> Nuevo {isClientes ? "cliente" : "proveedor"}
            </Button>
          </div>
        }
      />

      <div className="p-4">
        {(!isClientes || tab === "cta-cte") && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 border border-[#E5E5E5] bg-white rounded-sm overflow-hidden">
            <div className="px-4 py-2 border-b bg-neutral-50 flex items-center gap-2">
              <MagnifyingGlass size={14} className="text-[#666]" />
              <Input
                data-testid="search-cp"
                placeholder={`Buscar ${label.toLowerCase()} por nombre, CUIT o email…`}
                className="rounded-sm h-8 text-[12px] border-0 shadow-none focus-visible:ring-0 flex-1"
                value={searchCP}
                onChange={(e) => setSearchCP(e.target.value)}
              />
              {searchCP && (
                <button onClick={() => setSearchCP("")}
                  className="text-[#999] hover:text-[#666]"><X size={12} /></button>
              )}
              <span className="text-[10px] font-mono text-[#999] tracking-widest">
                {filteredItems.length}/{items.length}
              </span>
            </div>
            {/* Filtros por saldo */}
            <div className="px-4 py-2 border-b bg-white flex items-center gap-1">
              {[
                { v: "todos", label: "Todos", count: items.length },
                { v: "con_saldo", label: "Con saldo", count: items.filter((c) => Math.abs((c.saldo_ars || 0) + (c.saldo_usd || 0)) >= 0.01).length },
                { v: "sin_saldo", label: "Sin saldo", count: items.filter((c) => Math.abs((c.saldo_ars || 0) + (c.saldo_usd || 0)) < 0.01).length },
              ].map((opt) => (
                <button
                  key={opt.v}
                  data-testid={`filter-${opt.v}`}
                  onClick={() => setFilterSaldo(opt.v)}
                  className={`text-[11px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-sm transition-colors ${
                    filterSaldo === opt.v
                      ? "bg-[#6B1F24] text-white"
                      : "text-[#666] hover:bg-neutral-100"
                  }`}
                >
                  {opt.label} <span className={filterSaldo === opt.v ? "opacity-70" : "text-[#999]"}>({opt.count})</span>
                </button>
              ))}
            </div>
            <div className="max-h-[65vh] overflow-y-auto">
              <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead className="text-[11px] uppercase font-mono text-[#666] bg-neutral-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left">Nombre / CUIT</th>
                      <th className="px-4 py-2 text-right w-28">Saldo</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((c) => (
                      <tr key={c.id}
                        onClick={() => openDetail(c)}
                        data-testid={`cp-row-${c.id}`}
                        className={`cursor-pointer hover:bg-neutral-50 group ${selected?.id === c.id ? "bg-[#F5F1E9]" : ""}`}>
                        <td className="px-4 py-2 border-t border-[#E5E5E5]">
                          <div className="font-medium">{c.nombre}</div>
                          <div className="text-[11px] text-[#999] font-mono">{c.cuit || "sin CUIT"}</div>
                        </td>
                        <td className={`px-4 py-2 text-right font-mono border-t border-[#E5E5E5] ${c.saldo_ars > 0 ? "num-neg" : c.saldo_ars < 0 ? "num-pos" : ""}`}>
                          {fmtMoney(c.saldo_ars)}
                        </td>
                        <td className="px-2 py-2 border-t border-[#E5E5E5] text-center">
                          <div className="flex justify-center gap-1 opacity-0 group-hover:opacity-100">
                            <button
                              data-testid={`edit-cp-${c.id}`}
                              onClick={(e) => { e.stopPropagation(); openEdit(c); }}
                              className="p-1 hover:bg-neutral-200 rounded-sm text-[#666] hover:text-[#1A1A1A]"
                              title="Editar"
                            >
                              <PencilSimple size={13} />
                            </button>
                            <button
                              data-testid={`del-cp-${c.id}`}
                              onClick={(e) => { e.stopPropagation(); setDelCP(c); }}
                              className="p-1 hover:bg-red-50 rounded-sm text-[#666] hover:text-red-600"
                              title="Eliminar"
                            >
                              <Trash size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredItems.length === 0 && (
                      <tr><td colSpan={3} className="text-center py-8 text-[#999]">Sin resultados.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3 border border-[#E5E5E5] bg-white rounded-sm overflow-hidden">
            {!selected ? (
              <div className="p-10 text-center text-[#999] text-sm">
                Seleccioná {isClientes ? "un cliente" : "un proveedor"} para ver el detalle.
              </div>
            ) : (
              <>
                <div className="px-5 py-3 border-b border-[#E5E5E5] flex items-center justify-between">
                  <div>
                    <div className="font-heading font-semibold flex items-center gap-2">
                      {selected.nombre}
                      <button data-testid="edit-selected" onClick={() => openEdit(selected)}
                        className="p-1 rounded-sm text-[#666] hover:bg-neutral-100 hover:text-[#6B1F24]" title="Editar">
                        <PencilSimple size={13} />
                      </button>
                      <button data-testid="del-selected" onClick={() => setDelCP(selected)}
                        className="p-1 rounded-sm text-[#666] hover:bg-red-50 hover:text-red-600" title="Eliminar">
                        <Trash size={13} />
                      </button>
                    </div>
                    <div className="text-[11px] text-[#666] font-mono">
                      {selected.cuit && <>{selected.cuit} · </>}
                      Saldo: <span className={selected.saldo_ars > 0 ? "num-neg" : "num-pos"}>{fmtMoney(selected.saldo_ars)}</span>
                      {selected.saldo_usd > 0 && <> · USD {fmtMoney(selected.saldo_usd, "USD")}</>}
                      {!rangoActivo && movs && Math.abs((totales.debe - totales.haber) - (selected.saldo_ars || 0)) > 0.01 && (
                        <button
                          type="button"
                          onClick={recalcularSaldo}
                          data-testid="recalcular-saldo-btn"
                          title={`El saldo guardado (${fmtMoney(selected.saldo_ars)}) no coincide con el total de la cuenta corriente (${fmtMoney(totales.debe - totales.haber)}). Click para recalcularlo desde los movimientos.`}
                          className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-amber-800 bg-amber-50 border border-amber-300 rounded-sm px-1.5 py-0.5 hover:bg-amber-100"
                        >
                          ⚠ difiere del mayor · recalcular
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button data-testid="export-excel-btn" size="sm" variant="outline"
                      onClick={exportExcel}
                      className="rounded-sm h-8 text-[11px]">
                      <FileXls size={13} className="mr-1" /> Excel
                    </Button>
                    <Button data-testid="export-pdf-btn" size="sm" variant="outline"
                      onClick={exportPDF}
                      className="rounded-sm h-8 text-[11px]">
                      <FilePdf size={13} className="mr-1" /> PDF
                    </Button>
                    <Button
                      data-testid="new-ajuste-btn"
                      size="sm" variant="outline"
                      className="rounded-sm h-8 text-[11px] border-amber-300 text-amber-800 hover:bg-amber-50 ml-1"
                      onClick={() => {
                        setAjusteEditando(null);
                        setAjusteSigno(1);
                        setAjusteForm({
                          fecha: new Date().toISOString().slice(0, 10),
                          monto: "",
                          concepto: "Saldo inicial",
                          moneda: "ARS",
                        });
                        setOpenAjuste(true);
                      }}
                    >
                      <Plus size={13} className="mr-1" /> Ajuste +/−
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-sm h-8 ml-1 border-emerald-400 text-emerald-800 hover:bg-emerald-50"
                      onClick={() => setImputarOpen(true)}
                      data-testid="btn-imputar-saldos"
                    >
                      <ArrowRight size={13} className="mr-1" /> Imputar saldos
                    </Button>
                    <Sheet open={openMov} onOpenChange={setOpenMov}>
                      <SheetTrigger asChild>
                        <Button data-testid="new-cobropago-btn" size="sm" className="rounded-sm bg-[#6B1F24] hover:bg-[#571519] h-8 ml-1">
                          <ArrowRight size={14} className="mr-1" /> {isClientes ? "Cobrar" : "Pagar"}
                        </Button>
                      </SheetTrigger>
                      <SheetContent className="w-full sm:w-[560px] sm:max-w-[560px] overflow-y-auto">
                        <SheetHeader><SheetTitle className="font-heading">Registrar {isClientes ? "cobro" : "pago"}</SheetTitle></SheetHeader>
                        <form onSubmit={registrarMov} className="space-y-3 mt-6">
                          <div className="grid grid-cols-2 gap-3">
                            <div><Label className="text-[12px]">Fecha</Label>
                              <DateInput className="rounded-sm mt-1 font-mono" value={movForm.fecha}
                                onChange={(e) => setMovForm({ ...movForm, fecha: e.target.value })} required />
                            </div>
                            <div><Label className="text-[12px]">Moneda</Label>
                              <Select value={movForm.moneda} onValueChange={(v) => setMovForm({ ...movForm, moneda: v })}>
                                <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="ARS">ARS</SelectItem>
                                  <SelectItem value="USD">USD</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div><Label className="text-[12px]">Medio de pago</Label>
                            <Select value={movForm.caja_id} onValueChange={(v) => setMovForm({ ...movForm, caja_id: v })}>
                              <SelectTrigger data-testid="mov-caja" className="rounded-sm mt-1"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                              <SelectContent>
                                {mediosPago.length > 0
                                  ? mediosPago.filter((m) => m.caja_id).map((m) => (
                                      <SelectItem key={m.id} value={m.caja_id}>
                                        <div className="flex items-center gap-2">
                                          <span className="text-[10px] font-mono uppercase text-[#666] bg-neutral-100 px-1.5 py-0.5 rounded-sm">
                                            {m.tipo}
                                          </span>
                                          <span>{m.nombre}</span>
                                          {m.caja_nombre && m.caja_nombre !== m.nombre && (
                                            <span className="text-[10px] font-mono text-neutral-400">→ {m.caja_nombre}</span>
                                          )}
                                        </div>
                                      </SelectItem>
                                    ))
                                  : cajas.map((c) => (
                                      <SelectItem key={c.id} value={c.id}>{c.nombre} ({c.moneda})</SelectItem>
                                    ))}
                              </SelectContent>
                            </Select>
                            {mediosPago.length === 0 && (
                              <div className="text-[10px] text-amber-700 font-mono mt-1">
                                No hay medios de pago configurados. Configuralos en Administración → Medios de Pago.
                              </div>
                            )}
                          </div>

                          {/* Bloque cheque recibido - solo en COBRO con caja tipo=cheques */}
                          {isClientes && esCajaCheques && (
                            <div className="border border-emerald-200 rounded-sm bg-emerald-50/40" data-testid="cheque-recibido-block">
                              <div className="px-3 py-2 border-b border-emerald-200 bg-emerald-50 text-[11px] uppercase font-mono text-emerald-800 tracking-widest">
                                Datos del cheque recibido (obligatorio)
                              </div>
                              {retencionesTotal > 0 && (
                                <div className="px-3 py-2 bg-amber-50 border-b border-amber-200 text-[11px] font-mono text-amber-800" data-testid="cheque-neto-aviso">
                                  El cheque se registrará por <b>{fmtMoney(netoCaja, movForm.moneda)}</b> (lo que dice el cheque). El cobro total que baja de la cuenta corriente será <b>{fmtMoney(totalCobro, movForm.moneda)}</b> (cheque + retenciones {fmtMoney(retencionesTotal, movForm.moneda)}). Las retenciones no se pueden usar para pagar: sólo el cheque.
                                </div>
                              )}
                              <div className="p-3 grid grid-cols-2 gap-3">
                                <div className="col-span-2">
                                  <Label className="text-[11px]">Recibido de *</Label>
                                  <Input
                                    data-testid="cheque-recibido-por"
                                    className="rounded-sm mt-1 font-mono text-[12px]"
                                    placeholder="Nombre de quien nos entregó el cheque"
                                    value={movForm.cheque_info.recibido_por || ""}
                                    onChange={(e) => setMovForm({
                                      ...movForm,
                                      cheque_info: { ...movForm.cheque_info, recibido_por: e.target.value }
                                    })}
                                  />
                                  <div className="text-[10px] text-[#999] mt-1 font-mono">
                                    Auto-completado con el cliente. Editá si el cheque viene de un tercero.
                                  </div>
                                </div>
                                <div className="col-span-2">
                                  <Label className="text-[11px]">Banco emisor *</Label>
                                  <div className="mt-1">
                                    <BancoCombobox
                                      testId="cheque-banco"
                                      value={movForm.cheque_info.banco_code ? { code: movForm.cheque_info.banco_code, name: movForm.cheque_info.banco_nombre } : null}
                                      onChange={(b) => setMovForm({
                                        ...movForm,
                                        cheque_info: { ...movForm.cheque_info, banco_code: b.code, banco_nombre: b.name }
                                      })}
                                    />
                                  </div>
                                </div>
                                <div>
                                  <Label className="text-[11px]">N° de cheque *</Label>
                                  <Input
                                    data-testid="cheque-numero"
                                    className="rounded-sm mt-1 font-mono"
                                    placeholder="00012345"
                                    value={movForm.cheque_info.numero}
                                    onChange={(e) => setMovForm({
                                      ...movForm,
                                      cheque_info: { ...movForm.cheque_info, numero: e.target.value }
                                    })}
                                  />
                                </div>
                                <div>
                                  <Label className="text-[11px]">Firmante (opcional)</Label>
                                  <Input
                                    className="rounded-sm mt-1 text-[12px]"
                                    placeholder="Nombre del emisor"
                                    value={movForm.cheque_info.firmante || ""}
                                    onChange={(e) => setMovForm({
                                      ...movForm,
                                      cheque_info: { ...movForm.cheque_info, firmante: e.target.value }
                                    })}
                                  />
                                </div>
                                <div>
                                  <Label className="text-[11px]">Fecha de emisión *</Label>
                                  <DateInput
                                    data-testid="cheque-fecha-emision"
                                    className="rounded-sm mt-1 font-mono"
                                    value={movForm.cheque_info.fecha_emision}
                                    onChange={(e) => setMovForm({
                                      ...movForm,
                                      cheque_info: { ...movForm.cheque_info, fecha_emision: e.target.value }
                                    })}
                                  />
                                </div>
                                <div>
                                  <Label className="text-[11px]">Fecha de cobro *</Label>
                                  <DateInput
                                    data-testid="cheque-fecha-cobro"
                                    className="rounded-sm mt-1 font-mono"
                                    value={movForm.cheque_info.fecha_cobro}
                                    onChange={(e) => setMovForm({
                                      ...movForm,
                                      cheque_info: { ...movForm.cheque_info, fecha_cobro: e.target.value }
                                    })}
                                  />
                                </div>
                                <div className="col-span-2">
                                  <Label className="text-[11px]">Observaciones (opcional)</Label>
                                  <Input
                                    className="rounded-sm mt-1 text-[12px]"
                                    placeholder="Notas internas del cheque"
                                    value={movForm.cheque_info.observaciones || ""}
                                    onChange={(e) => setMovForm({
                                      ...movForm,
                                      cheque_info: { ...movForm.cheque_info, observaciones: e.target.value }
                                    })}
                                  />
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Bloque endoso de cheques - solo en PAGO con caja tipo=cheques */}
                          {!isClientes && esCajaCheques && (
                            <div className="border border-blue-200 rounded-sm bg-blue-50/40" data-testid="cheques-endoso-block">
                              <div className="px-3 py-2 border-b border-blue-200 bg-blue-50 flex items-center justify-between">
                                <span className="text-[11px] uppercase font-mono text-blue-800 tracking-widest">
                                  Cheques a endosar (obligatorio)
                                </span>
                                <span className="text-[10px] font-mono text-blue-700">
                                  Seleccionados: {(movForm.cheques_endosados_ids || []).length} · Total: {fmtMoney(
                                    chequesAceptados
                                      .filter((c) => movForm.cheques_endosados_ids.includes(c.id))
                                      .reduce((a, b) => a + b.monto, 0),
                                    movForm.moneda
                                  )}
                                </span>
                              </div>
                              <div className="max-h-56 overflow-y-auto">
                                {chequesAceptados.length === 0 ? (
                                  <div className="p-3 text-[12px] text-[#999]">
                                    No hay cheques en estado "Aceptado" para endosar.
                                  </div>
                                ) : (
                                  chequesAceptados.map((c) => {
                                    const checked = movForm.cheques_endosados_ids.includes(c.id);
                                    return (
                                      <label key={c.id} data-testid={`cheque-endoso-${c.id}`}
                                        className="flex items-center gap-2 px-3 py-2 text-[12px] hover:bg-blue-50/60 cursor-pointer border-b border-blue-100">
                                        <input
                                          type="checkbox" checked={checked}
                                          onChange={() => {
                                            const next = checked
                                              ? movForm.cheques_endosados_ids.filter((id) => id !== c.id)
                                              : [...movForm.cheques_endosados_ids, c.id];
                                            // Auto-sumar el monto de los seleccionados
                                            const suma = chequesAceptados
                                              .filter((x) => next.includes(x.id))
                                              .reduce((a, b) => a + b.monto, 0);
                                            setMovForm({
                                              ...movForm,
                                              cheques_endosados_ids: next,
                                              monto: suma.toFixed(2),
                                            });
                                          }}
                                        />
                                        <span className="font-mono font-semibold">{c.numero}</span>
                                        <span className="text-[#666] text-[11px]">{c.banco_nombre}</span>
                                        <span className="text-[11px] text-[#999]">Cobra: {fmtDate(c.fecha_cobro)}</span>
                                        <span className="ml-auto font-mono">{fmtMoney(c.monto, c.moneda)}</span>
                                        <span className="text-[10px] text-[#999]">de {c.cliente_nombre}</span>
                                      </label>
                                    );
                                  })
                                )}
                              </div>
                            </div>
                          )}
                          <div><Label className="text-[12px]">{isClientes ? (esCajaCheques ? "Importe del cheque (neto, lo que dice el papel)" : "Monto recibido (neto de retenciones)") : "Monto"}</Label>
                            <MoneyInput data-testid="mov-monto" step="0.01" className="rounded-sm mt-1 font-mono text-right"
                              value={movForm.monto} onChange={(e) => setMovForm({ ...movForm, monto: e.target.value })} required />
                          </div>
                          <div><Label className="text-[12px]">Concepto</Label>
                            <Input className="rounded-sm mt-1" value={movForm.concepto}
                              onChange={(e) => setMovForm({ ...movForm, concepto: e.target.value })} />
                          </div>

                          {facturasPend.length > 0 && (
                            <div className="border border-[#E5E5E5] rounded-sm">
                              <div className="px-3 py-2 bg-neutral-50 border-b border-[#E5E5E5]">
                                <div className="text-[11px] uppercase font-mono text-[#666] tracking-widest">
                                  Imputación a facturas pendientes
                                </div>
                                <div className="text-[10px] text-[#999] font-mono mt-0.5">
                                  Se aplica automáticamente por orden de vencimiento (más antigua primero).
                                  Si el monto no completa una factura queda como parcial.
                                  Podés seleccionar manualmente si querés forzar otra imputación.
                                </div>
                              </div>
                              <div className="max-h-40 overflow-y-auto">
                                {facturasPend.map((f, idx) => {
                                  const vencida = f.fecha_vencimiento && f.fecha_vencimiento < todayISO();
                                  return (
                                    <label key={f.id}
                                      className={`flex items-center gap-2 px-3 py-1.5 text-[12px] hover:bg-neutral-50 cursor-pointer border-b border-[#F5F5F5] ${
                                        vencida ? "bg-red-50/40" : ""
                                      }`}>
                                      <input type="checkbox" checked={movForm.facturas_aplicadas.includes(f.id)}
                                        onChange={() => toggleFactura(f.id)} />
                                      <span className="text-[10px] text-[#999] w-4">{idx + 1}</span>
                                      <span className="font-mono">{f.numero}</span>
                                      <span className="text-[#666] text-[11px]">
                                        Vto: {f.fecha_vencimiento ? fmtDate(f.fecha_vencimiento) : fmtDate(f.fecha)}
                                      </span>
                                      {vencida && <span className="text-[10px] text-red-700 font-semibold uppercase">Vencida</span>}
                                      <span className="ml-auto font-mono font-semibold">{fmtMoney(f.saldo, f.moneda)}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {isClientes && (
                            <div className="border border-[#E5E5E5] rounded-sm">
                              <div className="px-3 py-2 bg-neutral-50 flex items-center justify-between">
                                <span className="text-[11px] uppercase font-mono text-[#666]">
                                  Retenciones (nos las retiene el cliente)
                                </span>
                                <Button type="button" onClick={addRetencion} size="sm" variant="outline"
                                  data-testid="add-retencion-btn"
                                  className="rounded-sm h-7 text-[11px]">
                                  <Plus size={12} className="mr-1" /> Agregar
                                </Button>
                              </div>
                              <div className="divide-y">
                                {movForm.retenciones.length === 0 && (
                                  <div className="p-3 text-[12px] text-[#999]">
                                    Sin retenciones. Agregá una si el cliente practicó retenciones.
                                  </div>
                                )}
                                {movForm.retenciones.map((r, i) => (
                                  <div key={i} className="p-3 grid grid-cols-12 gap-2 items-end">
                                    <div className="col-span-5">
                                      <Label className="text-[10px]">Tipo</Label>
                                      <Select value={r.tipo} onValueChange={(v) => setRetencion(i, "tipo", v)}>
                                        <SelectTrigger className="rounded-sm h-8 mt-1 text-[12px]"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                          {TIPOS_TRIBUTO.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div className="col-span-3">
                                      <Label className="text-[10px]">Monto</Label>
                                      <NumberInput className="rounded-sm h-8 font-mono text-[12px] text-right" step="0.01"
                                        value={r.monto} onChange={(e) => setRetencion(i, "monto", e.target.value)}
                                        data-testid={`retencion-monto-${i}`} />
                                    </div>
                                    <div className="col-span-3">
                                      <Label className="text-[10px]">Obs.</Label>
                                      <Input className="rounded-sm h-8 text-[12px]" value={r.observacion || ""}
                                        onChange={(e) => setRetencion(i, "observacion", e.target.value)} />
                                    </div>
                                    <button type="button" onClick={() => removeRetencion(i)}
                                      className="col-span-1 text-red-600 hover:text-red-800 pt-4">
                                      <Trash size={14} />
                                    </button>
                                  </div>
                                ))}
                              </div>
                              {movForm.retenciones.length > 0 && (
                                <div className="px-3 py-2 bg-neutral-50 border-t border-[#E5E5E5] text-[12px] font-mono flex justify-between">
                                  <span className="text-[#666]">Retenciones total</span>
                                  <span className="text-[#B84A52]">{fmtMoney(retencionesTotal, movForm.moneda)}</span>
                                </div>
                              )}
                              <div className="px-3 py-2 border-t border-[#E5E5E5] text-[12px] font-mono flex justify-between bg-[#F5F1E9]" data-testid="cobro-total-resumen">
                                <span className="uppercase text-[#666] text-[11px]">Total del cobro (baja de cta. cte.)</span>
                                <span className="font-semibold">{fmtMoney(totalCobro, movForm.moneda)}</span>
                              </div>
                            </div>
                          )}
                          <Button data-testid="save-cobropago-btn" type="submit" className="w-full rounded-sm bg-[#6B1F24] hover:bg-[#571519]">
                            Confirmar
                          </Button>
                        </form>
                      </SheetContent>
                    </Sheet>
                  </div>
                </div>

                {/* Filtros por fecha */}
                <div className="px-5 py-2 border-b border-[#E5E5E5] bg-neutral-50 flex items-center gap-3 text-[11px]">
                  <span className="uppercase font-mono text-[#666] tracking-widest">Período:</span>
                  <label className="flex items-center gap-1">
                    <span className="text-[#666]">Desde</span>
                    <DateInput data-testid="fecha-desde" value={fechaDesde}
                      onChange={(e) => setFechaDesde(e.target.value)}
                      className="rounded-sm h-7 text-[11px] font-mono w-36" />
                  </label>
                  <label className="flex items-center gap-1">
                    <span className="text-[#666]">Hasta</span>
                    <DateInput data-testid="fecha-hasta" value={fechaHasta}
                      onChange={(e) => setFechaHasta(e.target.value)}
                      className="rounded-sm h-7 text-[11px] font-mono w-36" />
                  </label>
                  {(fechaDesde || fechaHasta) && (
                    <button onClick={clearFechas}
                      className="text-[11px] text-[#666] hover:text-[#6B1F24] flex items-center gap-1 px-2 py-0.5">
                      <X size={11} /> Limpiar
                    </button>
                  )}
                  <span className="ml-auto text-[#666] font-mono">
                    {movimientosUnificados.length} movimientos ·
                    Debe: <span className="text-[#B84A52]">{fmtMoney(totales.debe)}</span> ·
                    Haber: <span className="text-emerald-700">{fmtMoney(totales.haber)}</span>
                  </span>
                </div>

                {/* Alerta: saldo residual — sugerir redondeo a $0 */}
                {selected && Math.abs(selected.saldo_ars || 0) > 0.01 && Math.abs(selected.saldo_ars || 0) < 100 && (
                  <div
                    data-testid="alerta-redondeo"
                    className="mx-4 mt-3 border border-amber-300 bg-amber-50 rounded-sm px-3 py-2 flex items-center gap-3 text-[12px]"
                  >
                    <Warning size={16} weight="fill" className="text-amber-600 shrink-0" />
                    <div className="flex-1">
                      <div className="font-mono">
                        Saldo residual de <strong>{fmtMoney(selected.saldo_ars)}</strong>. ¿Redondearlo a $0?
                      </div>
                      <div className="text-[10px] text-amber-800/80 mt-0.5">
                        Se creará un ajuste automático por <span className="font-mono">{fmtMoney(-selected.saldo_ars)}</span> con concepto "Redondeo de saldo residual".
                      </div>
                    </div>
                    <Button
                      data-testid="btn-redondear"
                      size="sm"
                      className="rounded-sm bg-amber-600 hover:bg-amber-700 text-white h-7 text-[11px]"
                      onClick={redondearSaldoARS}
                    >
                      Redondear ahora
                    </Button>
                  </div>
                )}

                {/* Tabla unificada de movimientos */}
                <div className="max-h-[60vh] overflow-y-auto">
                  <div className="overflow-x-auto">
                    <table className="w-full text-[13px] grid-table">
                      <thead className="text-[11px] uppercase font-mono text-[#666] bg-neutral-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left w-24">Fecha</th>
                          <th className="px-4 py-2 text-left w-20">Tipo</th>
                          <th className="px-4 py-2 text-left">Referencia / Concepto</th>
                          <th className="px-4 py-2 text-right w-28">Debe</th>
                          <th className="px-4 py-2 text-right w-28">Haber</th>
                          <th className="px-4 py-2 text-right w-28">Saldo</th>
                          <th className="px-4 py-2 text-center w-16"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {movimientosUnificados.map((r) => {
                          const esMov = r.tipo !== "Factura";
                          const facEstadoStyle = {
                            pendiente: "bg-amber-50 text-amber-700 border-amber-200",
                            parcial: "bg-blue-50 text-blue-700 border-blue-200",
                            pagada: "bg-emerald-50 text-emerald-700 border-emerald-200",
                            anulada: "bg-red-50 text-red-700 border-red-200 line-through",
                            aplicada: "bg-purple-50 text-purple-700 border-purple-200",
                          };
                          return (
                          <tr key={r.key} data-testid={`mov-${r.key}`} className="hover:bg-neutral-50 group">
                            <td className="px-4 py-2 font-mono text-[12px]">{fmtDate(r.fecha)}</td>
                            <td className="px-4 py-2">
                              <span className={`inline-block px-1.5 py-0.5 rounded-sm text-[10px] font-mono ${
                                r.tipo === "Factura" ? "bg-red-50 text-red-700"
                                : r.tipo === "Nota de Crédito" ? "bg-amber-50 text-amber-800 border border-amber-200"
                                : "bg-emerald-50 text-emerald-700"
                              }`}>{r.tipo}</span>
                            </td>
                            <td className="px-4 py-2">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-mono text-[12px]">{r.referencia}</span>
                                {isClientes && r.raw?.tipo_comprobante && r.raw.tipo_comprobante !== "AJUSTE" && (
                                  <button
                                    data-testid={`preview-fac-${r.raw.id}`}
                                    onClick={() => setPreviewFac(r.raw)}
                                    className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded-sm text-[10px] text-[#666] hover:text-[#921A0E] hover:bg-red-50"
                                    title="Previsualizar comprobante"
                                  >
                                    <Eye size={12} /> Ver
                                  </button>
                                )}
                                {r.tipo === "Factura" && r.estado && (
                                  <span
                                    data-testid={`fac-estado-${r.raw?.id}`}
                                    className={`inline-block px-1.5 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-widest border ${
                                      facEstadoStyle[r.estado] || "bg-neutral-50 text-neutral-600 border-neutral-200"
                                    }`}
                                  >
                                    {r.estado}
                                  </span>
                                )}
                                {r.tipo === "Factura" && (r.raw?.saldo > 0.001) && (r.estado === "parcial" || r.estado === "pendiente") && (
                                  <span className="text-[10px] font-mono text-[#666]">
                                    saldo {fmtMoney(r.raw.saldo, r.moneda)}
                                  </span>
                                )}
                              </div>
                              {r.concepto && <div className="text-[10px] text-[#666] truncate max-w-md">{r.concepto}</div>}
                            </td>
                            <td className="px-4 py-2 text-right font-mono">
                              {r.debe > 0 ? <span className="text-[#B84A52]">{fmtMoney(r.debe, r.moneda)}</span> : ""}
                              {r.monto_anulado > 0 && !r.al_haber && <span className="text-neutral-400 line-through" title="Anulado: no afecta el saldo" data-testid={`anulado-monto-${r.raw.id}`}>{fmtMoney(r.monto_anulado, r.moneda)}</span>}
                            </td>
                            <td className="px-4 py-2 text-right font-mono">
                              {r.haber > 0 ? <span className="text-emerald-700">{fmtMoney(r.haber, r.moneda)}</span> : ""}
                              {r.monto_anulado > 0 && r.al_haber && <span className="text-neutral-400 line-through" title="Anulado: no afecta el saldo">{fmtMoney(r.monto_anulado, r.moneda)}</span>}
                            </td>
                            <td className={`px-4 py-2 text-right font-mono font-semibold ${
                              r.saldo_acumulado > 0 ? "num-neg" : r.saldo_acumulado < 0 ? "num-pos" : ""
                            }`}>
                              {fmtMoney(r.saldo_acumulado, r.moneda)}
                            </td>
                            <td className="px-2 py-2 text-center">
                              {esMov && (
                                <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <button
                                    data-testid={`edit-mov-${r.raw.id}`}
                                    onClick={() => openEditMov(r.raw)}
                                    className="p-1 rounded-sm text-[#666] hover:text-[#921A0E] hover:bg-red-50"
                                    title="Editar movimiento"
                                  >
                                    <PencilSimple size={13} />
                                  </button>
                                  <button
                                    data-testid={`del-mov-${r.raw.id}`}
                                    onClick={() => setDelMov(r.raw)}
                                    className="p-1 rounded-sm text-[#666] hover:text-red-700 hover:bg-red-50"
                                    title="Eliminar movimiento (revierte saldo)"
                                  >
                                    <Trash size={13} />
                                  </button>
                                </div>
                              )}
                              {r.raw?.tipo_comprobante === "AJUSTE" && (
                                <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                  {r.raw.estado !== "anulada" && (
                                    <>
                                      <button
                                        data-testid={`edit-ajuste-${r.raw.id}`}
                                        onClick={() => openEditAjuste(r.raw)}
                                        className="p-1 rounded-sm text-[#666] hover:text-amber-800 hover:bg-amber-50"
                                        title={
                                          ((r.raw.total || 0) > 0 && Math.abs((r.raw.saldo || 0) - (r.raw.total || 0)) > 0.01)
                                            ? `Editar ajuste (respeta los cobros/pagos ya aplicados; monto mínimo: ${((r.raw.total || 0) - (r.raw.saldo || 0)).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`
                                            : "Editar ajuste"
                                        }
                                      >
                                        <PencilSimple size={13} />
                                      </button>
                                      <button
                                        data-testid={`anular-ajuste-${r.raw.id}`}
                                        onClick={() => setAnulandoAjuste(r.raw)}
                                        className="p-1 rounded-sm text-[#666] hover:text-red-700 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                        title={
                                          ((r.raw.total || 0) > 0 && Math.abs((r.raw.saldo || 0) - (r.raw.total || 0)) > 0.01)
                                            ? "No se puede anular: el ajuste ya tiene cobros/pagos aplicados"
                                            : "Anular ajuste (deja marca fiscal)"
                                        }
                                        disabled={((r.raw.total || 0) > 0 && Math.abs((r.raw.saldo || 0) - (r.raw.total || 0)) > 0.01)}
                                      >
                                        <XCircle size={13} />
                                      </button>
                                    </>
                                  )}
                                  {r.raw.estado === "anulada" && (
                                    <span
                                      className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-neutral-100 text-neutral-500 border border-neutral-300"
                                      title={`Anulado el ${r.raw.anulada_at ? new Date(r.raw.anulada_at).toLocaleDateString("es-AR") : ""}`}
                                    >
                                      Anulado
                                    </span>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        );})}
                        {movimientosUnificados.length === 0 && (
                          <tr><td colSpan={7} className="text-center py-8 text-[#999]">
                            Sin movimientos en el período seleccionado.
                          </td></tr>
                        )}
                      </tbody>
                      {movimientosUnificados.length > 0 && (
                        <tfoot className="bg-neutral-50 border-t-2 border-[#E5E5E5]">
                          <tr>
                            <td colSpan={3} className="px-4 py-2 text-right font-mono uppercase text-[11px] text-[#666] tracking-widest">Totales</td>
                            <td className="px-4 py-2 text-right font-mono font-semibold text-[#B84A52]">{fmtMoney(totales.debe)}</td>
                            <td className="px-4 py-2 text-right font-mono font-semibold text-emerald-700">{fmtMoney(totales.haber)}</td>
                            <td className={`px-4 py-2 text-right font-mono font-bold ${
                              totales.debe - totales.haber > 0 ? "num-neg" : "num-pos"
                            }`}>{fmtMoney(totales.debe - totales.haber)}</td>
                            <td></td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        )}

        {isClientes && tab === "historial" && (
          <div className="border border-[#E5E5E5] bg-white rounded-sm overflow-hidden">
            <div className="px-5 py-3 border-b bg-neutral-50">
              <div className="font-heading font-semibold text-[15px] flex items-center gap-2">
                <FileXls size={16} weight="bold" className="text-[#6B1F24]" />
                Historial de cobros importados desde Excel
              </div>
              <div className="text-[11px] text-[#666] mt-0.5">
                Cada lote puede revertirse completo: se eliminan los cobros, se recalculan los saldos de clientes, cajas y facturas.
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px] grid-table">
                <thead className="text-[11px] uppercase font-mono text-[#666] bg-neutral-50 border-t">
                  <tr>
                    <th className="px-5 py-2 text-left w-40">Fecha carga</th>
                    <th className="px-5 py-2 text-left">Archivo</th>
                    <th className="px-5 py-2 text-right w-24">Cobros</th>
                    <th className="px-5 py-2 text-right w-28">Facturas</th>
                    <th className="px-5 py-2 text-left">Distribución</th>
                    <th className="px-5 py-2 text-center w-40">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {batches.map((b) => (
                    <tr key={b.id} data-testid={`kbatch-${b.id}`} className="group">
                      <td className="px-5 py-2 font-mono text-[12px]">
                        {new Date(b.created_at).toLocaleString("es-AR")}
                      </td>
                      <td className="px-5 py-2 truncate max-w-md" title={b.filename}>{b.filename}</td>
                      <td className="px-5 py-2 text-right font-mono">{b.count}</td>
                      <td className="px-5 py-2 text-right font-mono text-[#666]">{b.facturas_procesadas || "-"}</td>
                      <td className="px-5 py-2">
                        {b.por_caja ? (
                          <div className="flex flex-wrap gap-1">
                            {Object.entries(b.por_caja).map(([caja, monto]) => (
                              <span key={caja} className="text-[10px] font-mono bg-[#F5F1E9] text-[#6B1F24] px-1.5 py-0.5 rounded-sm">
                                {caja}: {fmtMoney(monto)}
                              </span>
                            ))}
                          </div>
                        ) : <span className="text-[#999] text-[11px]">-</span>}
                      </td>
                      <td className="px-5 py-2 text-center">
                        <div className="inline-flex items-center gap-1">
                          <button onClick={() => setViewBatch(b)} data-testid={`view-kbatch-${b.id}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-sm text-[11px] text-[#6B1F24] hover:bg-[#F5F1E9] border border-[#E5E5E5]">
                            <Eye size={12} /> Ver
                          </button>
                          <button onClick={() => setDelBatch(b)} data-testid={`del-kbatch-${b.id}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-sm text-[11px] text-red-700 hover:bg-red-50 border border-red-200">
                            <Trash size={12} /> Revertir
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {batches.length === 0 && (
                    <tr><td colSpan={6} className="text-center py-12 text-[#999]">Aún no hay importaciones de cobros.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Sheet crear/editar contraparte */}
      <Sheet open={openForm} onOpenChange={(v) => { setOpenForm(v); if (!v) { setEditingId(null); setForm(emptyCP()); } }}>
        <SheetContent className="w-full sm:w-[520px] sm:max-w-[520px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-heading">
              {editingId ? `Editar ${isClientes ? "cliente" : "proveedor"}` : `Nuevo ${isClientes ? "cliente" : "proveedor"}`}
            </SheetTitle>
          </SheetHeader>
          <form onSubmit={saveContraparte} className="space-y-3 mt-6">
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label className="text-[12px]">Nombre / Razón Social</Label>
                <Input className="rounded-sm mt-1" required value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="cp-nombre" />
              </div>
              <div>
                <Label className="text-[12px]">CUIT / DNI</Label>
                <div className="flex gap-1 mt-1">
                  <Input className="rounded-sm font-mono flex-1" value={form.cuit}
                    onChange={(e) => setForm({ ...form, cuit: e.target.value })}
                    data-testid="cp-cuit"
                    placeholder="30-12345678-9" />
                  <Button
                    type="button"
                    data-testid="cp-consultar-padron"
                    variant="outline"
                    size="sm"
                    className="rounded-sm h-9 text-[11px] px-2 whitespace-nowrap"
                    disabled={consultandoPadron}
                    onClick={consultarPadron}
                    title="Autocompletar datos consultando AFIP por CUIT"
                  >
                    {consultandoPadron ? "..." : "AFIP"}
                  </Button>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[12px]">Email</Label>
                <Input className="rounded-sm mt-1" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div><Label className="text-[12px]">Teléfono</Label>
                <Input className="rounded-sm mt-1" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-[12px]">Buscador de dirección (autocompletado)</Label>
              <div className="mt-1">
                <AddressAutocomplete
                  value={form.direccion}
                  onChange={(v) => setForm({ ...form, direccion: v })}
                  onSelect={(place) => setForm((f) => ({
                    ...f,
                    direccion: place.direccion_calle || f.direccion,
                    direccion_numero: place.direccion_numero || f.direccion_numero,
                    localidad: place.direccion_localidad || f.localidad,
                    provincia: place.direccion_provincia || f.provincia,
                    codigo_postal: place.direccion_cp || f.codigo_postal,
                  }))}
                  placeholder="Ej: Av. Corrientes 1234, Buenos Aires"
                  testId="cliente-direccion-autocomplete"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="col-span-3"><Label className="text-[12px]">Calle</Label>
                <Input className="rounded-sm mt-1" value={form.direccion}
                  onChange={(e) => setForm({ ...form, direccion: e.target.value })} />
              </div>
              <div><Label className="text-[12px]">Número</Label>
                <Input className="rounded-sm mt-1" value={form.direccion_numero}
                  onChange={(e) => setForm({ ...form, direccion_numero: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-[12px]">Localidad</Label>
                <Input className="rounded-sm mt-1" value={form.localidad}
                  onChange={(e) => setForm({ ...form, localidad: e.target.value })} />
              </div>
              <div><Label className="text-[12px]">Provincia</Label>
                <Input className="rounded-sm mt-1" value={form.provincia}
                  onChange={(e) => setForm({ ...form, provincia: e.target.value })} />
              </div>
              <div><Label className="text-[12px]">C. Postal</Label>
                <Input className="rounded-sm mt-1 font-mono" value={form.codigo_postal}
                  onChange={(e) => setForm({ ...form, codigo_postal: e.target.value })} />
              </div>
            </div>
            <div><Label className="text-[12px]">Condición IVA</Label>
              <Select value={form.condicion_iva} onValueChange={(v) => setForm({ ...form, condicion_iva: v })}>
                <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Responsable Inscripto">Responsable Inscripto</SelectItem>
                  <SelectItem value="Monotributista">Monotributista</SelectItem>
                  <SelectItem value="Consumidor Final">Consumidor Final</SelectItem>
                  <SelectItem value="Exento">Exento</SelectItem>
                  <SelectItem value="No Responsable">No Responsable</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {!isClientes && (
              <div>
                <Label className="text-[12px]">Tipo de proveedor</Label>
                <Select
                  value={form.tipo_proveedor || "productos"}
                  onValueChange={(v) => setForm({ ...form, tipo_proveedor: v })}
                >
                  <SelectTrigger className="rounded-sm mt-1" data-testid="cp-tipo-proveedor"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="productos">
                      Productos vendibles (ej: proveedor de carne, verduras)
                    </SelectItem>
                    <SelectItem value="servicios_insumos">
                      Servicios / Insumos (ej: luz, marketing, papelería)
                    </SelectItem>
                  </SelectContent>
                </Select>
                <div className="text-[10px] font-mono text-[#999] mt-1">
                  Determina qué se carga al hacer una compra: lista de productos o de insumos + concepto libre.
                </div>
                <div className="mt-2 space-y-1.5 border-t border-dashed pt-2">
                  <div className="text-[10px] uppercase font-mono text-[#666] tracking-widest">
                    Reglas por defecto al crear una compra
                  </div>
                  <label className="flex items-center gap-2 text-[12px] cursor-pointer">
                    <input
                      type="checkbox"
                      className="accent-[#6B1F24]"
                      checked={!!form.auto_actualiza_stock}
                      onChange={(e) => setForm({ ...form, auto_actualiza_stock: e.target.checked })}
                      data-testid="cp-auto-stock"
                    />
                    Marcar <strong>"Actualizar stock"</strong> automáticamente
                  </label>
                  <label className="flex items-center gap-2 text-[12px] cursor-pointer">
                    <input
                      type="checkbox"
                      className="accent-[#6B1F24]"
                      checked={!!form.auto_actualiza_costo}
                      onChange={(e) => setForm({ ...form, auto_actualiza_costo: e.target.checked })}
                      data-testid="cp-auto-costo"
                    />
                    Marcar <strong>"Actualizar costo de última compra"</strong> automáticamente
                  </label>
                </div>
                {form.tipo_proveedor === "servicios_insumos" && (
                  <div className="mt-3 border-t border-dashed pt-2">
                    <div className="text-[10px] uppercase font-mono text-[#666] tracking-widest mb-1">
                      Cuenta contable de gasto (opcional)
                    </div>
                    <CuentaCombobox
                      value={form.cuenta_gasto_default || ""}
                      onChange={(codigo) => setForm({ ...form, cuenta_gasto_default: codigo })}
                      cuentas={(cuentas || []).filter((c) => c.imputable && c.tipo === "egreso")}
                      placeholder="Elegí la cuenta de gasto para futuras compras…"
                      testid="cp-cuenta-gasto-default"
                      compact
                    />
                    <div className="text-[10px] font-mono text-[#999] mt-1">
                      Si asignás una cuenta, las próximas compras de este proveedor imputarán directo sin pasar por "Compras a Clasificar".
                    </div>
                  </div>
                )}
              </div>
            )}
            {isClientes && (
              <div>
                <Label className="text-[12px]">Lista de precios predeterminada</Label>
                <Select
                  value={form.lista_precios_id || "__none__"}
                  onValueChange={(v) => setForm({ ...form, lista_precios_id: v === "__none__" ? "" : v })}
                >
                  <SelectTrigger data-testid="cp-lista-precios" className="rounded-sm mt-1">
                    <SelectValue placeholder="Sin lista asignada (usa la primera al vender)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">
                      <span className="text-[#999]">Sin lista asignada</span>
                    </SelectItem>
                    {listasPrecios.map((l) => (
                      <SelectItem key={l.id} value={l.id}>{l.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="text-[10px] text-[#666] font-mono mt-1">
                  Se pre-selecciona al crear una venta a este cliente. Se puede sobreescribir en cada factura.
                </div>
              </div>
            )}
            {isClientes && (
              <div>
                <Label className="text-[12px]">Grupo (Mayorista / Minorista / etc.)</Label>
                <div className="flex items-center gap-1 mt-1">
                  <Select
                    value={form.grupo || "__none__"}
                    onValueChange={async (v) => {
                      if (v === "__new__") {
                        const nombre = window.prompt("Nombre del nuevo grupo:", "");
                        if (!nombre || !nombre.trim()) return;
                        try {
                          const { data } = await api.post("/clientes/grupos", { nombre: nombre.trim() });
                          const nuevos = [...gruposClientes, data].sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""));
                          setGruposClientes(nuevos);
                          setForm((f) => ({ ...f, grupo: data.nombre }));
                          toast.success(`Grupo "${data.nombre}" creado`);
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "No se pudo crear el grupo");
                        }
                        return;
                      }
                      setForm({ ...form, grupo: v === "__none__" ? "" : v });
                    }}
                  >
                    <SelectTrigger data-testid="cp-grupo-cliente" className="rounded-sm flex-1">
                      <SelectValue placeholder="Sin grupo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">
                        <span className="text-[#999]">Sin grupo</span>
                      </SelectItem>
                      {gruposClientes.map((g) => (
                        <SelectItem key={g.id} value={g.nombre}>
                          {g.nombre}
                          {g.cant_clientes > 0 && (
                            <span className="ml-2 text-[10px] font-mono text-neutral-400">
                              ({g.cant_clientes})
                            </span>
                          )}
                        </SelectItem>
                      ))}
                      <SelectItem value="__new__">
                        <span className="text-[#6B1F24] font-bold">+ Nuevo grupo…</span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {form.grupo && (() => {
                    const g = gruposClientes.find((x) => x.nombre === form.grupo);
                    if (!g) return null;
                    return (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        title={g.cant_clientes > 0
                          ? `No se puede eliminar: ${g.cant_clientes} cliente(s) usan este grupo`
                          : `Eliminar grupo "${g.nombre}"`}
                        disabled={g.cant_clientes > 0}
                        onClick={async () => {
                          if (!window.confirm(`¿Eliminar el grupo "${g.nombre}"?`)) return;
                          try {
                            await api.delete(`/clientes/grupos/${g.id}`);
                            setGruposClientes(gruposClientes.filter((x) => x.id !== g.id));
                            setForm((f) => ({ ...f, grupo: "" }));
                            toast.success(`Grupo "${g.nombre}" eliminado`);
                          } catch (e) {
                            const d = e?.response?.data?.detail;
                            toast.error(typeof d === "object" ? d?.message || "No se pudo eliminar" : (d || "No se pudo eliminar"));
                          }
                        }}
                        className="h-9 w-9 p-0 text-rose-600 hover:bg-rose-50 disabled:opacity-30"
                        data-testid="cp-grupo-cliente-borrar"
                      >
                        <Trash size={13} weight="bold" />
                      </Button>
                    );
                  })()}
                </div>
                <div className="text-[10px] text-[#666] font-mono mt-1">
                  Se usa en el indicador "Frecuencia de Compra por Cliente" del Dashboard.
                </div>
              </div>
            )}
            {isClientes && (
              <div>
                <Label className="text-[12px]">Cuenta contable de ingreso (opcional)</Label>
                <div className="mt-1">
                  <CuentaCombobox
                    value={form.cuenta_ingreso_default || ""}
                    onChange={(codigo) => setForm({ ...form, cuenta_ingreso_default: codigo })}
                    cuentas={(cuentas || []).filter((c) => c.imputable && c.tipo === "ingreso")}
                    placeholder="Elegí la cuenta de ingreso para futuras ventas…"
                    testid="cli-cuenta-ingreso-default"
                    compact
                  />
                </div>
                <div className="text-[10px] text-[#666] font-mono mt-1">
                  Si asignás una cuenta, las ventas a este cliente impactan directo ahí en Sumas y Saldos. Sino se usa la cuenta configurada por unidad de negocio o "Venta de Bienes".
                </div>
              </div>
            )}
            <div><Label className="text-[12px]">Observaciones</Label>
              <Input className="rounded-sm mt-1" value={form.observaciones}
                onChange={(e) => setForm({ ...form, observaciones: e.target.value })} />
            </div>
            {/* Condición de pago */}
            <div className="border border-[#E5E5E5] rounded-sm bg-neutral-50/50">
              <div className="px-3 py-2 border-b bg-neutral-50 flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-widest font-mono text-[#6B1F24]">
                  Condición de pago
                </span>
                <span className="text-[10px] font-mono text-[#666]">
                  Se aplica al crear {isClientes ? "ventas" : "compras"}
                </span>
              </div>
              <div className="p-3 space-y-2">
                <div>
                  <Label className="text-[12px]">Tipo</Label>
                  <Select value={form.cp_tipo} onValueChange={(v) => setForm({ ...form, cp_tipo: v })}>
                    <SelectTrigger data-testid="cp-condpago-tipo" className="rounded-sm mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="contado">Contado (vence el mismo día)</SelectItem>
                      <SelectItem value="dias">A X días</SelectItem>
                      <SelectItem value="dia_semana">Día fijo de la semana</SelectItem>
                      <SelectItem value="sin_plazo">Sin plazo determinado (sin vencimiento)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.cp_tipo === "dias" && (
                  <div>
                    <Label className="text-[12px]">Cantidad de días</Label>
                    <NumberInput
                      data-testid="cp-condpago-dias" min="0" step="1"
                      className="rounded-sm mt-1 font-mono"
                      value={form.cp_dias}
                      onChange={(e) => setForm({ ...form, cp_dias: e.target.value })}
                    />
                    <div className="text-[10px] text-[#666] font-mono mt-1">
                      Ej: 30 → vencimiento a los 30 días de emitido.
                    </div>
                  </div>
                )}
                {form.cp_tipo === "dia_semana" && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[12px]">Día</Label>
                      <Select
                        value={String(form.cp_dia_semana)}
                        onValueChange={(v) => setForm({ ...form, cp_dia_semana: parseInt(v, 10) })}
                      >
                        <SelectTrigger data-testid="cp-condpago-dia-semana" className="rounded-sm mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DIAS_SEMANA.map((d) => (
                            <SelectItem key={d.value} value={String(d.value)}>{d.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[12px]">Semana</Label>
                      <Select
                        value={String(form.cp_semanas_offset)}
                        onValueChange={(v) => setForm({ ...form, cp_semanas_offset: parseInt(v, 10) })}
                      >
                        <SelectTrigger data-testid="cp-condpago-offset" className="rounded-sm mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SEMANAS_OFFSET.map((s) => (
                            <SelectItem key={s.value} value={String(s.value)}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2 text-[10px] text-[#666] font-mono">
                      Ej: {DIAS_SEMANA.find((d) => d.value === Number(form.cp_dia_semana))?.label || "?"}{" "}
                      {SEMANAS_OFFSET.find((s) => s.value === Number(form.cp_semanas_offset))?.label || ""} respecto a la emisión.
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="text-[10px] text-[#999] font-mono border-t border-[#E5E5E5] pt-2">
              Auto-completado desde AFIP por CUIT · próximamente
            </div>
            <Button data-testid="save-cp-btn" type="submit" className="w-full rounded-sm bg-[#6B1F24] hover:bg-[#571519]">
              {editingId ? "Guardar cambios" : "Crear"}
            </Button>
          </form>
        </SheetContent>
      </Sheet>

      {/* Editar cobro/pago */}
      <Dialog open={!!editMov} onOpenChange={(v) => !v && setEditMov(null)}>
        <DialogContent className="max-w-md" data-testid="edit-mov-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Editar {isClientes ? "cobro" : "pago"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitEditMov} className="space-y-3">
            <div>
              <Label className="text-[12px]">Fecha</Label>
              <DateInput data-testid="edit-mov-fecha" className="rounded-sm mt-1 font-mono"
                value={editForm.fecha} onChange={(e) => setEditForm({ ...editForm, fecha: e.target.value })} required />
            </div>
            <div>
              <Label className="text-[12px]">Medio de pago</Label>
              <Select value={editForm.caja_id} onValueChange={(v) => setEditForm({ ...editForm, caja_id: v })}>
                <SelectTrigger data-testid="edit-mov-caja" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {mediosPago.length > 0
                    ? mediosPago
                        .filter((m) => m.caja_id)
                        // No permitimos reasignar a la caja de Cheques desde acá.
                        .filter((m) => {
                          const c = cajas.find((cx) => cx.id === m.caja_id);
                          return !c || c.tipo !== "cheques";
                        })
                        .map((m) => (
                          <SelectItem key={m.id} value={m.caja_id}>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-mono uppercase text-[#666] bg-neutral-100 px-1.5 py-0.5 rounded-sm">
                                {m.tipo}
                              </span>
                              <span>{m.nombre}</span>
                              {m.caja_nombre && m.caja_nombre !== m.nombre && (
                                <span className="text-[10px] font-mono text-neutral-400">→ {m.caja_nombre}</span>
                              )}
                            </div>
                          </SelectItem>
                        ))
                    : cajas.filter((c) => c.tipo !== "cheques").map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.nombre} ({c.moneda})</SelectItem>
                      ))}
                </SelectContent>
              </Select>
              <div className="text-[10px] text-[#999] mt-1 font-mono">
                Nota: no se puede reasignar a un medio que apunte a la caja de Cheques desde acá.
              </div>
            </div>
            <div>
              <Label className="text-[12px]">Monto</Label>
              <MoneyInput data-testid="edit-mov-monto" step="0.01" className="rounded-sm mt-1 font-mono text-right"
                value={editForm.monto} onChange={(e) => setEditForm({ ...editForm, monto: e.target.value })} required />
            </div>
            <div>
              <Label className="text-[12px]">Concepto</Label>
              <Input data-testid="edit-mov-concepto" className="rounded-sm mt-1"
                value={editForm.concepto} onChange={(e) => setEditForm({ ...editForm, concepto: e.target.value })} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setEditMov(null)} className="rounded-sm">Cancelar</Button>
              <Button type="submit" data-testid="edit-mov-submit" className="rounded-sm bg-[#921A0E] hover:bg-[#6B1F24]">
                Guardar cambios
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Eliminar cobro/pago (revierte todo) */}
      <AlertDialog open={!!delMov} onOpenChange={(v) => !v && setDelMov(null)}>
        <AlertDialogContent data-testid="del-mov-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading flex items-center gap-2">
              <Warning size={18} weight="fill" className="text-red-600" />
              Eliminar {isClientes ? "cobro" : "pago"}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-[13px]">
                <p>
                  Vas a eliminar {isClientes ? "el cobro" : "el pago"} del
                  {" "}<span className="font-mono font-semibold">{fmtDate(delMov?.fecha)}</span> por
                  {" "}<span className="font-mono font-semibold">{fmtMoney(delMov?.monto, delMov?.moneda)}</span>
                  {" "}({delMov?.caja_nombre}).
                </p>
                <p>
                  Se revierten automáticamente:
                </p>
                <ul className="list-disc pl-5 text-[12px] font-mono text-[#666]">
                  <li>El saldo de las facturas aplicadas.</li>
                  <li>El saldo del {isClientes ? "cliente" : "proveedor"} (vuelve a subir).</li>
                  <li>El saldo de la caja.</li>
                  <li>El movimiento de caja asociado.</li>
                  {isClientes ? (
                    <li>Si el cobro creó un cheque recibido, se elimina.</li>
                  ) : (
                    <li>Si el pago endosó cheques, vuelven a estar en cartera (Aceptado).</li>
                  )}
                </ul>
                <p className="text-[12px] text-red-700 font-semibold">Esta acción no se puede deshacer.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="del-mov-confirm"
              onClick={(e) => { e.preventDefault(); confirmDeleteMov(); }}
              className="rounded-sm bg-red-700 hover:bg-red-800"
            >
              Eliminar y revertir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* Duplicate upload prompt */}
      <AlertDialog open={!!dupPrompt} onOpenChange={(v) => { if (!v) { setDupPrompt(null); if (fileRef.current) fileRef.current.value = ""; } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading flex items-center gap-2">
              <Warning size={18} weight="fill" className="text-amber-600" />
              Archivo ya importado
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-[13px]">
                <p>Este archivo de cobros ya fue subido antes:</p>
                {dupPrompt && (
                  <div className="border border-[#E5E5E5] rounded-sm p-3 bg-neutral-50 text-[12px] font-mono space-y-1">
                    <div>Archivo: {dupPrompt.existing.filename}</div>
                    <div>Cargado: {new Date(dupPrompt.existing.created_at).toLocaleString("es-AR")}</div>
                    <div>Cobros creados: {dupPrompt.existing.count}</div>
                  </div>
                )}
                <p className="text-amber-700">¿Subirlo igual? Se generarán cobros duplicados.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => runImportCobros(dupPrompt.file, true)}
              className="rounded-sm bg-amber-600 hover:bg-amber-700">
              Subir igual
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Resultado import cobros */}
      <AlertDialog open={!!importResult} onOpenChange={(v) => !v && setImportResult(null)}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Cobros importados</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-[13px]">
                <div className="grid grid-cols-2 gap-3">
                  <div className="border border-[#E5E5E5] rounded-sm p-3 bg-emerald-50">
                    <div className="text-[10px] uppercase font-mono text-[#666] tracking-widest">Cobros creados</div>
                    <div className="font-mono text-xl font-semibold text-emerald-700">{importResult?.cobros_creados}</div>
                  </div>
                  <div className="border border-[#E5E5E5] rounded-sm p-3 bg-blue-50">
                    <div className="text-[10px] uppercase font-mono text-[#666] tracking-widest">Facturas procesadas</div>
                    <div className="font-mono text-xl font-semibold text-blue-700">{importResult?.facturas_procesadas}</div>
                  </div>
                </div>
                {importResult?.por_caja && Object.keys(importResult.por_caja).length > 0 && (
                  <div className="border border-[#E5E5E5] rounded-sm">
                    <div className="px-3 py-2 bg-neutral-50 text-[11px] uppercase font-mono text-[#666]">
                      Distribución por caja
                    </div>
                    <div className="divide-y">
                      {Object.entries(importResult.por_caja).map(([caja, monto]) => (
                        <div key={caja} className="px-3 py-2 flex justify-between text-[12px] font-mono">
                          <span>{caja}</span>
                          <span className="font-semibold">{fmtMoney(monto)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {(importResult?.saldo_pendiente_cc || 0) > 0 && (
                  <div className="text-[12px] p-3 bg-amber-50 border border-amber-200 rounded-sm">
                    <div className="font-mono text-amber-800">
                      Saldo pendiente en cuenta corriente:{" "}
                      <span className="font-semibold">{fmtMoney(importResult.saldo_pendiente_cc)}</span>
                    </div>
                    <div className="text-[11px] text-amber-700 mt-1">
                      Las facturas con C.C. quedaron pendientes (no generaron cobro).
                    </div>
                  </div>
                )}
                {importResult?.no_encontradas?.length > 0 && (
                  <div className="border border-red-200 bg-red-50 rounded-sm p-3">
                    <div className="text-[11px] uppercase font-mono text-red-700 tracking-widest mb-1">
                      Comprobantes no encontrados ({importResult.no_encontradas.length})
                    </div>
                    <div className="max-h-32 overflow-y-auto text-[11px] font-mono text-red-800 space-y-0.5">
                      {importResult.no_encontradas.map((c, i) => <div key={i}>· {c}</div>)}
                    </div>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction className="rounded-sm bg-[#6B1F24] hover:bg-[#571519]">Cerrar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Revertir batch de cobros */}
      <AlertDialog open={!!delBatch} onOpenChange={(v) => !v && setDelBatch(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading flex items-center gap-2">
              <Warning size={18} weight="fill" className="text-amber-600" />
              Revertir importación de cobros
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-[13px]">
                <p>¿Eliminar el lote <span className="font-mono">{delBatch?.filename}</span>?</p>
                <p>
                  Se borrarán <span className="font-mono font-semibold">{delBatch?.count}</span> cobros.
                  Los saldos de clientes, cajas y facturas se restaurarán al estado previo.
                </p>
                {delBatch?.por_caja && (
                  <div className="text-[11px] text-[#666] pt-1 border-t border-[#E5E5E5] mt-2">
                    <div className="font-mono uppercase tracking-widest mb-1">Cajas afectadas:</div>
                    {Object.entries(delBatch.por_caja).map(([caja, monto]) => (
                      <div key={caja} className="flex justify-between font-mono">
                        <span>{caja}</span>
                        <span>−{fmtMoney(monto)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-revert-kbatch" onClick={revertBatch}
              className="rounded-sm bg-red-600 hover:bg-red-700">
              Revertir lote
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {viewBatch && (
        <BatchDetailDialog batch={viewBatch}
          onClose={() => setViewBatch(null)}
          onChange={() => { load(); loadBatches(); if (selected) openDetail(selected); }} />
      )}

      {/* Eliminar cliente/proveedor - confirmación */}
      <AlertDialog open={!!delCP} onOpenChange={(v) => !v && setDelCP(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">
              Eliminar {isClientes ? "cliente" : "proveedor"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              ¿Eliminar <span className="font-mono">{delCP?.nombre}</span>?
              {delCP?.cuit && <> (CUIT {delCP.cuit})</>}
              <br />
              Solo se puede eliminar si no tiene facturas ni {isClientes ? "cobros" : "pagos"} asociados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-cp"
              onClick={confirmDeleteCP}
              className="rounded-sm bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bloqueo por referencias */}
      <AlertDialog open={!!delCPRefs} onOpenChange={(v) => !v && setDelCPRefs(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading flex items-center gap-2">
              <Warning size={18} weight="fill" className="text-amber-600" />
              No se puede eliminar
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-[13px]">
                <p>
                  <span className="font-mono">{delCPRefs?.cp?.nombre}</span> tiene comprobantes asociados y no puede eliminarse.
                </p>
                <div className="border border-amber-200 rounded-sm bg-amber-50 p-3 text-[12px] font-mono space-y-1">
                  <div>Facturas: <span className="font-semibold">{delCPRefs?.facturas || 0}</span></div>
                  <div>
                    {isClientes ? "Cobros" : "Pagos"}:{" "}
                    <span className="font-semibold">{delCPRefs?.cobros || delCPRefs?.pagos || 0}</span>
                  </div>
                </div>
                <p className="text-[12px] text-[#666] pt-1">
                  Eliminá primero los comprobantes desde{" "}
                  {isClientes ? "Ventas" : "Compras"} y{" "}
                  {isClientes ? "Cta. Cte. Clientes" : "Cta. Cte. Proveedores"}, o revertí el lote de importación correspondiente.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => setDelCPRefs(null)}
              className="rounded-sm bg-[#6B1F24] hover:bg-[#571519]"
            >
              Entendido
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog Ver listado de cobros cargados en el sistema */}
      <Dialog
        open={cobrosOpen}
        onOpenChange={async (v) => {
          setCobrosOpen(v);
          if (v) {
            setCobrosLoading(true);
            try {
              const { data } = await api.get("/cobros-pagos", { params: { tipo: "cobro" } });
              setCobrosList(data || []);
            } catch (_) {
              setCobrosList([]);
            } finally {
              setCobrosLoading(false);
            }
          }
        }}
      >
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <ListNumbers size={20} className="text-[#6B1F24]" />
              Cobros cargados en el sistema
            </DialogTitle>
            <DialogDescription className="text-[11px] text-[#666]">
              Últimos 1000 cobros ordenados por fecha (más reciente primero). Incluye cobros manuales, contra factura y por importación.
            </DialogDescription>
          </DialogHeader>
          {cobrosLoading ? (
            <div className="py-10 text-center text-[13px] text-[#999]">Cargando…</div>
          ) : cobrosList.length === 0 ? (
            <div className="py-10 text-center text-[13px] text-[#999]">Todavía no hay cobros cargados.</div>
          ) : (
            <div className="border border-[#E5E5E5] rounded-sm overflow-hidden">
              <table className="w-full text-[13px]">
                <thead className="bg-neutral-50 border-b border-[#E5E5E5]">
                  <tr className="text-left text-[11px] uppercase font-mono text-[#666]">
                    <th className="px-3 py-2">Fecha</th>
                    <th className="px-3 py-2">Cliente</th>
                    <th className="px-3 py-2">Caja / Banco</th>
                    <th className="px-3 py-2 text-right">Monto</th>
                    <th className="px-3 py-2">Facturas aplicadas</th>
                    <th className="px-3 py-2">Observaciones</th>
                  </tr>
                </thead>
                <tbody>
                  {cobrosList.map((c) => {
                    const facAplicadas = (c.facturas_aplicadas || []).length;
                    const moneda = c.moneda || "ARS";
                    return (
                      <tr key={c.id} className="border-t border-neutral-100 hover:bg-neutral-50" data-testid={`cobro-row-${c.id}`}>
                        <td className="px-3 py-2 font-mono text-[12px]">{fmtDate(c.fecha)}</td>
                        <td className="px-3 py-2 text-[12px] truncate max-w-[220px]" title={c.contraparte_nombre}>
                          {c.contraparte_nombre || "—"}
                        </td>
                        <td className="px-3 py-2 text-[12px]">
                          {c.caja_nombre || c.medio_pago_nombre || "—"}
                        </td>
                        <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-700">
                          {fmtMoney(c.monto, moneda)}
                        </td>
                        <td className="px-3 py-2 text-[11px] font-mono text-neutral-600">
                          {facAplicadas > 0 ? `${facAplicadas} factura(s)` : <span className="text-neutral-400">A cuenta</span>}
                        </td>
                        <td className="px-3 py-2 text-[11px] text-neutral-600 truncate max-w-[200px]" title={c.observaciones || ""}>
                          {c.observaciones || <span className="text-neutral-300">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Imputar saldos a favor (anticipos) */}
      <ImputarSaldosDialog
        open={imputarOpen}
        onClose={() => setImputarOpen(false)}
        contraparte={selected}
        tipo={tipo}
        onDone={() => {
          load();
          if (selected) openDetail(selected);
        }}
      />

      {/* Dialog Crear deuda / Saldo inicial / Editar ajuste */}
      <Dialog open={openAjuste} onOpenChange={(v) => {
        if (!savingAjuste) {
          setOpenAjuste(v);
          if (!v) setAjusteEditando(null);
        }
      }}>
        <DialogContent className="max-w-md rounded-sm" data-testid="ajuste-deuda-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {ajusteEditando ? "Editar ajuste" : "Ajuste de cuenta corriente"} de <span className="text-[#6B1F24]">{selected?.nombre}</span>
            </DialogTitle>
            <DialogDescription className="text-[11px] text-[#666]">
              Se agregará como comprobante tipo AJUSTE al saldo del {isClientes ? "cliente" : "proveedor"}. Útil para saldos iniciales o correcciones manuales.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitAjuste} className="space-y-3">
            {ajusteEditando && Math.abs((ajusteEditando.saldo || 0) - (ajusteEditando.total || 0)) > 0.01 && (
              <div className="text-[11px] font-mono border border-amber-200 bg-amber-50 text-amber-900 rounded-sm p-2">
                <div className="font-semibold">Este ajuste tiene cobros/pagos aplicados.</div>
                <div>Monto ya aplicado: <b>{fmtMoney((ajusteEditando.total || 0) - (ajusteEditando.saldo || 0), ajusteEditando.moneda)}</b></div>
                <div>El nuevo monto debe ser ≥ al ya aplicado. El saldo se recalcula automáticamente.</div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[12px]">Fecha</Label>
                <DateInput required
                  data-testid="ajuste-fecha" className="rounded-sm mt-1 font-mono"
                  value={ajusteForm.fecha}
                  onChange={(e) => setAjusteForm({ ...ajusteForm, fecha: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-[12px]">Moneda</Label>
                <Select
                  value={ajusteForm.moneda}
                  onValueChange={(v) => setAjusteForm({ ...ajusteForm, moneda: v })}
                >
                  <SelectTrigger data-testid="ajuste-moneda" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ARS">ARS</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-[12px]">Tipo de ajuste</Label>
              <div className="mt-1 grid grid-cols-2 gap-1" data-testid="ajuste-signo">
                <button type="button" onClick={() => setAjusteSigno(1)} data-testid="ajuste-signo-mas"
                  className={`h-9 rounded-sm border text-[12px] font-medium ${ajusteSigno > 0 ? "bg-[#B84A52] border-[#B84A52] text-white" : "bg-white border-neutral-300 text-neutral-700 hover:bg-neutral-50"}`}>
                  + Suma deuda {isClientes ? "(el cliente debe más)" : "(le debemos más)"}
                </button>
                <button type="button" onClick={() => setAjusteSigno(-1)} data-testid="ajuste-signo-menos"
                  className={`h-9 rounded-sm border text-[12px] font-medium ${ajusteSigno < 0 ? "bg-emerald-700 border-emerald-700 text-white" : "bg-white border-neutral-300 text-neutral-700 hover:bg-neutral-50"}`}>
                  − Resta deuda {isClientes ? "(a favor del cliente)" : "(a nuestro favor)"}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-[12px]">Monto</Label>
              <NumberInput step="0.01" required autoFocus min={0}
                data-testid="ajuste-monto"
                className="rounded-sm mt-1 font-mono text-right"
                value={ajusteForm.monto}
                onChange={(e) => setAjusteForm({ ...ajusteForm, monto: e.target.value })}
                placeholder="0.00"
              />
              <div className="text-[10px] text-neutral-500 mt-1">
                {ajusteSigno < 0
                  ? "Se registra como crédito (va al Haber) y baja el saldo. Queda disponible para imputar a comprobantes pendientes."
                  : "Se registra como deuda (va al Debe) y sube el saldo. Se puede cobrar/pagar como un comprobante más."}
              </div>
            </div>
            <div>
              <Label className="text-[12px]">Concepto</Label>
              <Input
                data-testid="ajuste-concepto"
                className="rounded-sm mt-1"
                value={ajusteForm.concepto}
                onChange={(e) => setAjusteForm({ ...ajusteForm, concepto: e.target.value })}
                placeholder="Saldo inicial / Ajuste manual / etc."
              />
            </div>
            <div className="text-[10px] text-[#666] font-mono">
              Aparecerá en la cta. cte. como <strong>AJUSTE-…</strong>. El saldo del {isClientes ? "cliente" : "proveedor"} se {ajusteForm.monto > 0 ? "incrementará" : "ajustará"} por este monto.
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" className="rounded-sm"
                onClick={() => setOpenAjuste(false)} disabled={savingAjuste}>
                Cancelar
              </Button>
              <Button
                type="submit"
                data-testid="ajuste-submit"
                className="rounded-sm bg-[#6B1F24] hover:bg-[#571519]"
                disabled={savingAjuste}
              >
                {savingAjuste ? "Guardando…" : ajusteEditando ? "Guardar" : ajusteSigno < 0 ? "Registrar crédito" : "Registrar deuda"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirm Anular Ajuste */}
      <Dialog open={!!anulandoAjuste} onOpenChange={(v) => { if (!v && !anularBusy) setAnulandoAjuste(null); }}>
        <DialogContent className="max-w-md rounded-sm" data-testid="anular-ajuste-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-red-700">
              <XCircle size={18} /> Anular ajuste
            </DialogTitle>
            <DialogDescription className="text-[11px] text-[#666]">
              El ajuste queda registrado con estado <b>Anulada</b> (marca fiscal). Se revierte el saldo del{" "}
              {isClientes ? "cliente" : "proveedor"} y el ajuste sigue visible en el historial.
            </DialogDescription>
          </DialogHeader>
          {anulandoAjuste && (
            <div className="text-[12px] font-mono border border-[#E5E5E5] rounded-sm p-3 bg-neutral-50">
              <div><b>{anulandoAjuste.numero}</b></div>
              <div className="text-[11px] text-[#666] mt-1">
                Fecha: {anulandoAjuste.fecha} · Monto: {fmtMoney(anulandoAjuste.total || 0, anulandoAjuste.moneda)}
              </div>
              {anulandoAjuste.observaciones && (
                <div className="text-[11px] text-[#666]">{anulandoAjuste.observaciones}</div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAnulandoAjuste(null)} disabled={anularBusy}>Cancelar</Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={confirmAnularAjuste}
              disabled={anularBusy}
              data-testid="confirm-anular-ajuste-btn"
            >
              {anularBusy ? "Anulando…" : "Anular ajuste"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <PdfPreviewDialog
        open={!!previewFac}
        onOpenChange={(o) => { if (!o) setPreviewFac(null); }}
        url={previewFac ? `/afip/pdf/${previewFac.id}` : null}
        title={previewFac ? `${previewFac.tipo_comprobante || ""} ${previewFac.numero || ""}`.trim() : ""}
        filename={previewFac ? `${previewFac.tipo_comprobante || "comprobante"}_${previewFac.numero || previewFac.id}.pdf`.replace(/[^\w.-]/g, "_") : undefined}
      />
    </>
  );
}
